import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  lazy,
  Suspense,
  Fragment,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import {
  PAID_PLAN_IDS,
  PRICING_TIERS,
  tierPriceLabels,
  formatPrice,
  EXTRA_IDEA_PRICE_CENTS,
  EXTRA_SESSIONS_PER_PACK,
  EXTRA_DEEP_WORK_IDEAS_PER_PACK,
  SINGLE_SESSION_PRICE_CENTS,
  type PlanId,
} from '../../../../methods/src/common/pricing';

import { useLocation, Link } from 'wouter';
import { HomeScrollCue } from '../components/HomeScrollCue';
import TextareaAutosize from 'react-textarea-autosize';
import { loadChunkWithRetry } from '../loadChunk';
import { writeAnonState, useAuthModal, useAuth } from '../state';
import { markLandingVisit, stampLandingVariant } from '../audience';
import { Wordmark, BrandLogo, Button } from '../components';
import { MarketingNav } from '../components/MarketingNav';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { HomePill } from '../components/HomePill';
// The hero itself (HeroShowcase — the deliverable "conveyor") stays EAGER: it's
// the LCP element and must paint as soon as the entry chunk boots. Everything
// BELOW the fold is code-split into lazy chunks (see the `lazy(...)` block
// below) so the entry chunk that mounts the hero is smaller and boots — and
// animates the conveyor — sooner.
import { HeroShowcase } from '../components/HeroShowcase';
import { FAQ_ITEMS } from '../content/faq';
// SiteFooter stays a static import: it's already in the entry chunk via
// AppShell, so code-splitting it here would move nothing out of the entry chunk.
import { SiteFooter } from '../SiteFooter';
import { ListedOnStrip } from '../components/ListedOnStrip';
import { openPrerenderGate } from '../prerender';
// Brand-mark rendering helper now lives in src/utils — same logic, shared
// across Landing FAQ answers, SurveyForm question labels, and any other
// surface where brand-mention text comes from a non-React source.
import { renderWithBrand } from '../utils/renderWithBrand';

// Interaction-gated modals — each split into its own async chunk so the entry
// bundle that paints the hero doesn't carry them. They're conditionally
// rendered (only mounted once their open flag flips true), so React.lazy fires
// the import on first open, not on page load. Neither is SEO content and
// neither is present at prerender-snapshot time, so this is invisible to the
// crawler and to LCP — it just removes the modal code + CSS from the
// critical-path bundle.
const SparkModal = lazy(() =>
  loadChunkWithRetry(() => import('../components/SparkModal')).then((m) => ({ default: m.SparkModal })),
);
const ConfidentialityModal = lazy(() =>
  loadChunkWithRetry(() => import('../components/ConfidentialityModal')).then((m) => ({ default: m.ConfidentialityModal })),
);

// ---------------------------------------------------------------------------
// Below-the-fold sections — code-split out of the entry chunk.
//
// These are the six largest components on the page (TheClimb, PricingStack,
// HowItWorksSteps, MarketplaceSpotlight, AdvisoryBoardDiagram, AskAiBand) and
// they ALL sit below the fold. Splitting them into their own async chunks
// shrinks the entry chunk that boots React and paints the hero — so the hero,
// and its deliverable "conveyor" animation, appear sooner.
//
// Unlike the modals above (which load on first open), these are page content, so
// the page PRELOADS them during idle time (see `belowReady` in LandingPage) and
// only renders them once the chunks are in hand — the hero's paint and first
// animation frames get the main thread first, and real users never see a
// Suspense fallback in their place. For the crawler, the page holds the
// prerender snapshot open (openPrerenderGate) until these chunks have loaded, so
// the headless snapshot still carries the full below-the-fold content.
//
// Each is only otherwise imported by lazy routes (Students, Plans, the A/B
// variants), so nothing entry-reachable pulls them in statically — which is what
// lets them actually leave the entry chunk.
const HowItWorksSteps = lazy(() =>
  loadChunkWithRetry(() => import('../components/HowItWorksSteps')).then((m) => ({ default: m.HowItWorksSteps })),
);
const AdvisoryBoardDiagram = lazy(() =>
  loadChunkWithRetry(() => import('../components/AdvisoryBoardDiagram')).then((m) => ({ default: m.AdvisoryBoardDiagram })),
);
const TheClimb = lazy(() =>
  loadChunkWithRetry(() => import('../components/TheClimb')).then((m) => ({ default: m.TheClimb })),
);
const MarketplaceSpotlight = lazy(() =>
  loadChunkWithRetry(() => import('../components/MarketplaceSpotlight')).then((m) => ({ default: m.MarketplaceSpotlight })),
);
const PricingStack = lazy(() =>
  loadChunkWithRetry(() => import('./variants/pricingStack')).then((m) => ({ default: m.PricingStack })),
);
const AskAiBand = lazy(() =>
  loadChunkWithRetry(() => import('../components/AskAiBand')).then((m) => ({ default: m.AskAiBand })),
);
// Product demo video — a click-to-play facade that sits immediately below the
// hero. The video file itself never loads until the user clicks, and the facade
// component is code-split here (like its neighbours) so it stays off the entry
// chunk. Both together keep it invisible to LCP and the Lighthouse score.
const DemoVideo = lazy(() =>
  loadChunkWithRetry(() => import('../components/DemoVideo')).then((m) => ({ default: m.DemoVideo })),
);

// The below-the-fold chunk preloaders, as a single list so the mount effect and
// the render gate agree on exactly what must be loaded before we drop the gate.
// import() is deduped, so these resolve against the same chunks React.lazy uses.
const preloadBelowFold = () =>
  Promise.all([
    loadChunkWithRetry(() => import('../components/DemoVideo')),
    loadChunkWithRetry(() => import('../components/HowItWorksSteps')),
    loadChunkWithRetry(() => import('../components/AdvisoryBoardDiagram')),
    loadChunkWithRetry(() => import('../components/TheClimb')),
    loadChunkWithRetry(() => import('../components/MarketplaceSpotlight')),
    loadChunkWithRetry(() => import('./variants/pricingStack')),
    loadChunkWithRetry(() => import('../components/AskAiBand')),
  ]);

// Renders a lazy below-the-fold section only once `ready` is true (the deferred
// chunks have preloaded). The <Suspense> is a safety net: by the time `ready`
// flips, the chunk is already in hand, so the fallback should never actually
// paint — it just satisfies React.lazy's contract of a boundary above it.
function Deferred({ ready, children }: { ready: boolean; children: ReactNode }) {
  if (!ready) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}

/**
 * Every subscription price shown on this page, read from the canonical pricing
 * table rather than typed as a literal.
 *
 * This page previously carried 31 hardcoded price strings. A price change would
 * have updated what Stripe charged while the landing page went on quoting the
 * old figure — the single most damaging place in the product for that to happen.
 * Interpolate these; never type a price.
 */
const STANDARD = tierPriceLabels('inventor');
const PLUS = tierPriceLabels('plus');
const PRO = tierPriceLabels('startup');

/**
 * The annual saving advertised on the billing toggle, as a whole percent.
 *
 * The FLOOR across all paid tiers, floored rather than rounded, so the claim is
 * true of every plan rather than only the best one. Today Standard saves 35% and
 * the other two save 33.3%, so this reads 33%.
 *
 * Derived rather than hardcoded: a literal here is the same drift risk as a
 * hardcoded price, and it fails in the worse direction — an overstated saving is
 * a false advertising claim rather than merely a stale figure.
 */
/**
 * The upright-bold runs inside the italic overage line.
 *
 * `marginLeft` is OPTICAL CORRECTION, not a missing space. The word space is
 * already present in the markup, but the surrounding text is italic and an
 * italic letterform slants forward into the space that follows it — so an
 * italic-to-upright transition ("for" -> "$14.99") reads as though the words
 * are touching even though they are not. A sliver of extra room restores the
 * gap the eye expects.
 *
 * Left side only. The right side is followed by a comma or a full stop, and
 * pushing those away from the word they belong to would look worse than the
 * problem being solved.
 */
const OVERAGE_EMPHASIS: CSSProperties = {
  color: 'var(--ink)',
  fontStyle: 'normal',
  marginLeft: '0.15em',
};

const ANNUAL_SAVING_PCT = Math.floor(
  Math.min(
    ...PAID_PLAN_IDS.map((id) => {
      const t = PRICING_TIERS[id];
      const monthly = t.monthToMonthCents ?? 0;
      const annual = t.monthlyEquivalentCents ?? 0;
      return monthly > 0 ? (1 - annual / monthly) * 100 : 0;
    }),
  ),
);

/* Homepage-top styles: the marketing nav (transparent->solid sticky bar)
   and the light editorial hero. Kept as one const so nav + hero read as a
   single masthead. Brass is rare (wordmark glyph + active underline); the
   one filled control is Heartwood. */
/**
 * Light A/B variant skin. Scoped under `.landing--light` so every rule outranks
 * the dark base by specificity. Same content/structure/funnel — only the visual
 * system flips: warm near-white ground (Linen), ink text, heartwood as the
 * headline accent (spark-green only on the CTA), and the busy motion purged.
 * Per the design expert's calm/minimalist spec.
 */
export const LANDING_LIGHT_CSS = `
  /* ---- grounds: dark bands flip to warm near-white ---- */
  .landing--light { background: #FBFAF6 !important; }
  .landing--light .lhero {
    background-color: #FBFAF6;
    background-image: none;
  }
  .landing--light .pvpanel,
  .landing--light .landing-final-account {
    background: #FBFAF6;
    background-image: none;
    border-top: 1px solid rgba(15,23,20,0.08);
  }

  /* ---- hero text: light-on-dark becomes ink-on-light ---- */
  .landing--light .lhero__headline,
  .landing--light .lhero__headline-line { color: #0F1714; }
  /* Accent word: bright spark-green glows on light and is brand-banned there,
     so the payoff accent becomes deep heartwood. */
  .landing--light .lhero__headline-accent-word { color: #1B3A2E; }
  /* Cap the headline scale — the 128px ceiling is part of the "shouting" read. */
  .landing--light .lhero__headline { font-size: clamp(44px, 5vw, 72px); }
  .landing--light .lhero__sub,
  .landing--light .lhero__sub-mobile { color: rgba(15,23,20,0.62); }
  /* New hero CTA cluster on the light variant. The cream entry-box keeps its
     dark-on-cream innards as-is; only the on-dark parchment bits (listed label,
     free line, spark pill) recolor to ink/brass for the light ground. */
  .landing--light .lhero__listed-label { color: rgba(15,23,20,0.5); }
  .landing--light .lhero__freeline { color: rgba(15,23,20,0.68); }
  .landing--light .lhero__sparkpill-q { color: rgba(15,23,20,0.6); }
  .landing--light .lhero__sparkpill-a { color: var(--brass-deep); }
  .landing--light .lhero__sparkpill { border-color: rgba(181,133,60,0.4); background: rgba(181,133,60,0.1); }

  /* Featured-on eyebrow was parchment (for dark); make it readable on light. */
  .landing--light .lclose__badges .listedon__eyebrow { color: var(--text-decorative); }

  /* ---- motion purge: the biggest "busy" signal ---- */
  /* Kill keyframe animations within the variant (conveyor roll, rotators,
     count-ups, reveals, ignition pulses). Transitions (hover) are left intact.
     Elements that used an entrance fade are forced visible so nothing that
     animated-in stays stuck at opacity 0. */
  .landing--light *,
  .landing--light *::before,
  .landing--light *::after {
    animation: none !important;
  }
  .landing--light .lhero__eyebrow,
  .landing--light .lhero__headline,
  .landing--light .lhero__sub,
  .landing--light .lhero__listed,
  .landing--light .lhero__form,
  .landing--light .lhero__subrow,
  .landing--light .lhero__pricing,
  .landing--light .lhero__demoline,
  .landing--light .hero-card,
  .landing--light .hero-card__seal {
    opacity: 1 !important;
    transform: none !important;
  }

  /* Soften card elevation to one calm shadow on the light ground. */
  .landing--light .hero-card {
    box-shadow: 0 1px 2px rgba(15,23,20,.03), 0 14px 34px rgba(15,23,20,.05);
  }

  /* ---- remaining dark sections flip to light ----
     Each of these full-dark blocks uses --parchment for its text on a
     heartwood ground. Remapping --parchment -> ink WITHIN the scoped block
     recolors all that light text to dark in one move, and we set the ground to
     Linen and drop any dark gradient. (Literal rgba(244,239,226,...) text — not
     the var — is caught explicitly below.) */
  .landing--light .mspot,
  .landing--light .climb__plate {
    background: #FBFAF6;
    background-image: none;
    --parchment: #0F1714;
    border: 1px solid rgba(15,23,20,0.08);
  }
  /* Climb accent word (was spark-light green, glows on light) -> heartwood. */
  .landing--light .climb__accent { color: #1B3A2E; }
  /* Marketplace: the intro paragraph and the sample-profile footnote use a
     hardcoded light rgba (not --parchment), so the var remap can't reach them.
     Force them to readable ink tones on the now-light ground. */
  .landing--light .mspot__sub { color: rgba(15,23,20,0.72); }
  .landing--light .mspot__foot { color: rgba(15,23,20,0.55); }

  /* The nav itself is handled by passing variant="solid" (dark wordmark + ink
     links) on the light variant — no CSS override needed. */

`;

export const LANDING_TOP_CSS = `

  /* Editorial split hero — a sibling of the sign-in page, presented as one
     bordered rounded card. Left: light Parchment content + form, leading the
     page. Right: dark Heartwood image panel carrying the sign-in pull-quote,
     byline, and microline. */
  /* The hero fills the viewport below the 72px nav so the card AND the
     credibility rotator both sit above the fold as one unit. */
  /* ===== Dark cinematic hero =====
     The whole first viewport is one full-bleed heartwood STAGE (unified with
     the nav above it) — no more cream card floating next to a green panel. Big
     asymmetric left-aligned type over a deep-green ground with a living
     spark-green accent reads premium and serious (which IS the headline). Cream
     owns everything below, so the page gets a "curtain up" rhythm. */
  .lhero {
    /* VISIBLE all-GREEN depth gradient (the earlier version was too subtle to
       read). Two parts, hue locked in the heartwood family — no brass/yellow:
       (1) a VERTICAL depth ramp that HOLDS --heartwood #1b3a2e through the top
       ~24% (the nav zone) so the stage blends seamlessly into the solid-heartwood
       nav with no seam, then deepens to near-ink green #0d1f16 at the base — a
       wide ~0.30 lightness spread so the gradient is clearly visible; (2) a
       lifted-green SPOTLIGHT (#34785a) behind the front-focus card slot
       (~70% 60%) so the focused deliverable literally sits in the light. Spark
       stays OUT of the background (it's the action color). */
    background-color: #1b3a2e;
    background-image:
      radial-gradient(58% 70% at 70% 58% in oklch,
        #34785a 0%,
        #245a42 42%,
        rgba(27, 58, 46, 0) 72%),
      linear-gradient(180deg,
        #1b3a2e 0%,
        #1b3a2e 24%,
        #153024 64%,
        #0d1f16 100%);
    /* Symmetric top/bottom padding + justify-content:center so the whole
       eyebrow→pricing group centers on the viewport as one block, rather than
       sitting biased low with a thin top gap (founder: "center this on this
       viewport, top padding too little"). min-height now subtracts the launch
       promo bar when present (--promo-h, set pre-paint by the gate script in
       index.html) so the stage sizes to the true fold instead of overshooting it
       by the promo's height. min-height (never a fixed/max height) keeps the
       group top-anchored — never clipped under the nav — when content is taller
       than the fold on short laptops. Desktop (>=1101px); the <=1100 / tablet /
       phone blocks set their own padding. */
    padding: clamp(32px, 4.5vh, 72px) clamp(24px, 4vw, 72px) clamp(32px, 4.5vh, 72px) clamp(40px, 7vw, 140px);
    min-height: calc(100svh - 100px - var(--promo-h, 0px));
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    /* Clip the deliverable cascade where it bleeds off the right edge — the
       "infinite canvas" feel. */
    overflow: hidden;
  }
  /* Green spotlight BLOOM — the same lifted green behind the focus slot,
     animated up 0→100% on load (the "curtain rising") to add dimensionality as
     the cards settle. No brass, no neon orb. */
  .lhero::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background: radial-gradient(46% 56% at 70% 58% in oklch,
      color-mix(in oklch, #34785a 60%, transparent) 0%,
      transparent 64%);
    opacity: 0;
    animation: heroPoolIn 700ms ease-out forwards;
    pointer-events: none;
  }
  @keyframes heroPoolIn { to { opacity: 1; } }
  /* VIGNETTE — darken the outer/bottom edges so the type + cards pop inward, in
     a deep GREEN-black (never adds warmth). Weighted toward the BOTTOM (center
     at 50% 26%) so the top edge stays clear and heartwood-matched for the nav. */
  .lhero::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: radial-gradient(135% 120% at 50% 26%,
      transparent 56%,
      rgba(8, 20, 14, 0.55) 100%);
  }
  /* Asymmetric two-column stage: content leads (~55%), the deliverable cascade
     takes the right and bleeds off the viewport edge. Everything sits above the
     depth pool (z-index 1). */
  .lhero__split {
    /* Natural height (was flex:1 1 auto). The parent .lhero now owns vertical
       centering via justify-content:center, so the split must be its content
       height for the eyebrow→pricing group to center as one block. The
       min-height below still reserves room for the card cascade. */
    flex: 0 0 auto;
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    display: grid;
    /* Right column widened further + gap tightened so the deliverable conveyor
       has as much horizontal room as possible to spread across (founder: "spread
       out even more horizontally"). Left stays 1fr so the big headline still fits. */
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.16fr);
    gap: clamp(16px, 2vw, 40px);
    align-items: center;
    min-height: clamp(420px, 64vh, 640px);
    position: relative;
    z-index: 1;
  }
  /* Left content — NO card chrome. The dark stage is the ground; the content
     floats directly on it, left-aligned and architectural. */
  .lhero__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    min-width: 0;
  }
  .lhero__main {
    width: 100%;
    max-width: 620px;
    text-align: left;
    display: flex;
    flex-direction: column;
  }
  /* Eyebrow — a small brass KICKER now (was the big 18-28px block). The
     headline is the star, so the audience-widening line shrinks to a mono
     kicker with a brass tick to its LEFT, left-aligned on the dark stage.
     On-dark brass (--brass-light) reads cleanly against the deep green. */
  .lhero__eyebrow {
    display: flex;
    align-items: center;
    gap: 16px;
    font-family: var(--f-mono);
    /* Dialed back down a touch (founder: "make the eyebrow font smaller"). */
    font-size: clamp(15px, 1.5vw, 21px);
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    text-align: left;
    color: var(--brass-light);
    margin-bottom: clamp(12px, 1.6vh, 20px);
    opacity: 0;
    animation: heroRiseIn 500ms cubic-bezier(0.22, 1, 0.36, 1) 40ms forwards;
  }
  .lhero__eyebrow::before {
    content: "";
    flex: none;
    width: 48px;
    height: 2px;
    background: var(--brass);
  }
  /* Top-banner variant — a single CENTERED line spanning the full hero width at
     the top, so it fills the empty space above the right-side card carousel
     (founder request). Brass ticks flank BOTH sides (the base gives ::before;
     this adds ::after). On desktop it holds to one line; it may wrap on phones. */
  .lhero__eyebrow--top {
    justify-content: center;
    align-self: stretch;
    text-align: center;
    /* Cancel the hero's ASYMMETRIC padding (left clamp(40,7vw,140) is bigger than
       right clamp(24,4vw,72)) with negative margins, then re-apply SYMMETRIC
       padding, so the eyebrow centers on the true page width instead of drifting
       right (founder: "center the eyebrow, it's geared toward the right edge"). */
    margin: clamp(4px, 1.4vh, 14px) calc(-1 * clamp(24px, 4vw, 72px)) clamp(2px, 1vh, 8px) calc(-1 * clamp(40px, 7vw, 140px));
    padding: 0 clamp(24px, 4vw, 72px);
    position: relative;
    z-index: 1;
  }
  .lhero__eyebrow--top::after {
    content: "";
    flex: none;
    width: 48px;
    height: 2px;
    background: var(--brass);
  }
  @media (min-width: 1025px) {
    /* Allow the top eyebrow to wrap to two centered lines on narrow desktops.
       It used to be no-wrap, but the eyebrow now leads with the "personal
       innovation OS" category label and the longer string would overflow the
       hero between ~1025 and 1300px. A two-line eyebrow flanked by the brass
       ticks reads fine; an overflowing one-liner does not. */
    .lhero__eyebrow--top { white-space: normal; }
    /* margin-top 0 (was clamp(16px, 4vh, 72px)): the old top-push existed because
       the split used flex:1 and centered ITS content, leaving the eyebrow pinned
       near the nav with a void beneath it on tall screens. Now .lhero centers the
       whole eyebrow→pricing group as one block (justify-content:center + split at
       natural height), so any eyebrow top-push would just double-count and re-open
       that void. Zeroed. */
    .lhero__eyebrow--top { margin-top: 0; }
  }
  /* Oversized, architectural, LEFT-ALIGNED headline — the moment. Presence
     from scale + tight leading/tracking (Sentient 400). Hard <br>s lock the
     3-line signpost ("Random spark" / "to your" / "business."); min(vw, vh)
     keeps it shrinking on short screens so the stage's form + rotator still
     clear the fold. Parchment on the dark ground. */
  .lhero__headline {
    /* POSTER VOICE — Cabinet Grotesk, the brand's display face (same typeface
       as the wordmark). Moved OFF the Sentient serif so the hero and the logo
       no longer read as two different brands sharing a viewport — the mismatch
       testers flagged. This is the "poster voice"; Sentient stays the editorial
       voice for in-flow section headings. The fallback stack (--f-display) is
       deliberately grotesque, never serif, so a font swap can't flip serif→sans
       on the biggest headline on the site.

       Weight logic: per founder, the whole headline runs a single uniform
       weight (800) — "Your spark / to a real / business." all read at the same
       heavy Cabinet Grotesk weight, so the setup lines match the "business."
       payoff. The distinction now comes from COLOR (parchment setup, spark-green
       payoff) + the brass underline flourish, not a weight step-down.

       Font stack is spelled out here (not the --f-display token) so it can
       insert 'Cabinet Fallback' — the metric-matched, local()-only face in
       index.html — BEFORE Switzer. Two payoffs: (1) before Cabinet loads, the
       real headline paints in a fallback whose glyph advances match Cabinet, so
       there's no horizontal reflow on this centered headline when Cabinet swaps
       in (previously it fell back to Switzer, a different-width sans, and
       reflowed on every cold load); (2) the desktop instant-hero overlay uses
       this exact same stack, so during the cross-fade both layers show identical
       glyphs in identical boxes in every font-load state — nothing to ghost.
       Scoped to this headline only, so other --f-display consumers are
       unaffected. Keep in sync with #instant-hero-desktop .ihd-h1 in index.html. */
    font-family: 'Cabinet Grotesk', 'Cabinet Fallback', 'Switzer', system-ui, sans-serif;
    font-weight: 800;
    /* Sized for a 3-LINE stack. Trimmed from the serif's ceiling (was
       clamp(62px, min(10vw,9.6vh), 138px)) because Cabinet Grotesk sets WIDER
       than Sentient at the same px — the trim keeps the three hard-broken lines
       in the left column and clear of the right-side showcase. Cabinet's fuller
       color makes this read as substantial as the old serif size. */
    font-size: clamp(60px, min(9.7vw, 9.9vh), 132px);
    line-height: 0.98;
    /* Looser tracking to match the now-uniform heavy weight — chunky 800 letters
       across the whole headline need air, not the tight -0.03em of the old
       Medium setup lines. */
    letter-spacing: -0.02em;
    color: var(--parchment);
    max-width: 14ch;
    text-align: left;
    margin: 0 0 clamp(14px, 1.7vh, 22px);
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }
  /* Each line wipes in left-to-right via a feathered mask sweep (reusing the
     closing-CTA "ink wipe"), staggered — a confident type reveal. Only
     mask-position/opacity animate, so zero layout shift (lines hold their box
     from first paint). */
  .lhero__headline-line {
    display: inline-block;
    -webkit-mask-image: linear-gradient(90deg, #000 45%, transparent 55%);
    mask-image: linear-gradient(90deg, #000 45%, transparent 55%);
    -webkit-mask-size: 220% 100%;
    mask-size: 220% 100%;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: 100% 0;
    mask-position: 100% 0;
    opacity: 0;
    animation: heroHeadlineWipe 820ms cubic-bezier(0.4, 0, 0.2, 1) 120ms forwards;
  }
  /* The middle line "to your" wipes in between line 1 and the accent, so the
     three lines cascade 1 → 2 → 3 rather than 1 + (2&3 together). */
  .lhero__headline-line--mid { animation-delay: 210ms; }
  /* "your" (on the middle line) is part of the green payoff "your business.",
     so it carries the spark-green accent color/weight — but NOT the underline
     flourish, which lives only under the final word "business." */
  .lhero__headline-accent-word {
    color: var(--spark-light);
    font-weight: 800;
  }
  /* The final line "business." — the SINGLE heavy moment: bright on-dark spark
     green at weight 800, echoing the wordmark's own weight + green so the payoff
     word literally rhymes with the logo. Everything else in the headline is
     Medium; this one word carries the punch. Looser tracking than the setup
     (-0.02em vs -0.03em) — chunky 800 letters need air, not tightening. */
  .lhero__headline-accent {
    color: var(--spark-light);
    font-weight: 800;
    letter-spacing: -0.02em;
    animation-delay: 300ms;
    /* Eye-catchy flourish: a loose, slightly-imperfect BRASS underline stroke
       beneath "business." (a hand-drawn feel, echoing the brand's
       illustrated-flourish vocabulary — not a straight border-bottom). Brass
       (per the founder) so the spark-green word gets a warm brass accent rather
       than a same-color underline. */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='14' viewBox='0 0 320 14' fill='none'%3E%3Cpath d='M4 9C54 4 120 3 176 6c40 2 96 6 140 2' stroke='%23C79A4E' stroke-width='4.5' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: 0 100%;
    background-size: 100% 0.16em;
    padding-bottom: 0.12em;
  }
  /* Recessive subhead — parchment at 0.82 (recession via size + opacity works
     here because the ground is dark; the brand's "body stays full-strength"
     rule is a light-ground rule). Left-aligned, tighter measure. */
  .lhero__sub {
    /* Bumped up again (founder: "make the subhead larger"). */
    font-size: clamp(18px, 1.5vw, 22px);
    line-height: 1.42;
    color: var(--parchment);
    /* Even out the multi-line wrap so the accent question isn't split with a
       lone "is" orphaned at a line end. */
    text-wrap: balance;
    max-width: 52ch;
    margin: 0 0 clamp(14px, 1.7vh, 20px);
    text-align: left;
    opacity: 0;
    animation: heroSubIn 560ms cubic-bezier(0.22, 1, 0.36, 1) 480ms forwards;
  }
  /* The destination phrase "the world." in BOLD brass (founder request) — a warm
     brass accent that lands the subhead's payoff. */
  .lhero__sub-accent { color: var(--brass-light); font-weight: 700; }
  /* Desktop-only forced break so line 2 reads "or a record of your contribution
     to the world." (founder request — line 1 was running too long). Hidden below
     desktop so tablet/phone wrap naturally. */
  .lhero__sub-br { display: none; }
  @media (min-width: 1025px) {
    .lhero__sub-br { display: inline; }
    /* Center the ENTIRE left column on one shared axis (founder: center the
       subhead, input, CTA, and microlines to match the headline; "taken" then
       naturally becomes the short MIDDLE line). Centered within the LEFT COLUMN,
       NOT the page. Each block is centered via margin-inline auto (not just
       text-align) so headline / subhead / form share the same center line. */
    .lhero__headline {
      text-align: center;
      margin-left: auto;
      margin-right: auto;
    }
    .lhero__headline-accent { text-align: center; }
    .lhero__sub {
      text-align: center;
      margin-left: auto;
      margin-right: auto;
    }
    .lhero__form { margin-left: auto; margin-right: auto; }
    .lhero__trustline { text-align: center; justify-content: center; }
    .lhero__pricing { justify-content: center; }
    .lhero__secondary { justify-content: center; }
  }
  /* The condensed mobile subhead is HIDDEN by default (desktop + tablet show the
     full .lhero__sub); only the ≤767 phone block reveals it. Without this base
     rule it leaked onto desktop as a duplicate second subhead. */
  .lhero__sub-mobile { display: none; }
  /* Form rises into place a beat after the headline (message + CTA reachable
     within ~1s — never withheld). Capped narrower than the text measure so the
     input reads as a focused object, not a full-width bar. */
  .lhero__form {
    display: flex;
    flex-direction: column;
    gap: clamp(10px, 1.4vh, 14px);
    width: 100%;
    /* 520 (was 480) matches /ve's wider composer, so the under-box row (Spark
       pill + free/no-card line) fits on one line instead of wrapping. */
    max-width: 520px;
    margin-bottom: clamp(12px, 1.8vh, 20px);
    opacity: 0;
    transform: translateY(16px);
    animation: heroRiseIn 560ms cubic-bezier(0.22, 1, 0.36, 1) 340ms forwards;
  }
  /* The idea input stays CREAM on the dark stage — the brightest, most
     inviting object in the viewport (its prominence goes UP versus the old
     cream-on-cream card). A soft stacked shadow lifts it off the deep green. */
  /* Idea composer — cream entry-box CARD floating on the dark hero (ported
     from /ve). The card is cream #FBF8F0 (matching the page's material, not
     pure white) lifted by a green-black shadow; NO hairline (the shadow does
     the lift on dark). Everything INSIDE follows light-ground rules. */
  .lhero__box {
    background: #FBF8F0;
    border-radius: 12px;
    padding: 15px 16px 13px;
    box-shadow: 0 2px 8px rgba(9,15,12,0.22), 0 20px 44px rgba(9,15,12,0.42);
    transition: box-shadow 160ms ease;
  }
  .lhero__box:focus-within {
    box-shadow: 0 0 0 3px rgba(79,217,146,0.28), 0 2px 8px rgba(9,15,12,0.22), 0 20px 44px rgba(9,15,12,0.42);
  }
  .lhero__boxinput {
    display: block; width: 100%; border: none; background: none; resize: none;
    font-family: var(--f-serif); font-style: italic; font-size: 18px; line-height: 1.5;
    color: var(--ink); padding: 0; margin: 0 0 12px; outline: none;
    min-height: clamp(48px, 5vh, 72px);
  }
  .lhero__boxinput::placeholder { color: var(--text-decorative); }
  .lhero__box-foot {
    display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
  }
  /* Confidentiality line — light-ground values (inside the cream card): mono,
     ink-muted, brass lock icon. Sits right at the cursor where the fear peaks. */
  .lhero__box-privacy {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: var(--f-mono); font-size: 10px; letter-spacing: 0.02em; line-height: 1.4;
    color: rgba(24,23,18,0.62); max-width: 220px;
  }
  .lhero__box-privacy svg { width: 13px; height: 13px; flex: 0 0 auto; color: #B5853C; }
  /* The form's only button is now the docked verdict CTA — hug its label
     (not full width) and dock it to the card's bottom-right. Spark green
     #1F9D63 (via variant="spark", the light-ground primary) + ink text. */
  .lhero__form .btn {
    width: auto;
    flex: none;
    font-size: 15px;
    font-weight: 600;
    padding: 12px 20px;
    white-space: nowrap;
  }
  /* ONE consolidated trust line at the point of action (was a mono receipt +
     a separate wax-seal ledger — the fuller ledger now lives just below the
     fold). Names the free terms and the #1 fear in one line, with "Never used
     to train AI" a brass link to the protection panel. On-dark colors. */
  /* "Listed on" trust row — two curated marks (Capterra + Product Hunt) above
     the headline. On the dark hero the logos sit in light pills (matching the
     page's marquee badge treatment) so both read cleanly and consistently as a
     pair. min-height reserves the row so the logos loading can't reflow the
     headline below. */
  .lhero__listed {
    display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
    margin: 0 0 20px; min-height: 34px;
    opacity: 0; transform: translateY(16px);
    animation: heroRiseIn 560ms cubic-bezier(0.22, 1, 0.36, 1) 220ms forwards;
  }
  /* Centered full-width variant — sits directly under the eyebrow banner,
     spanning the hero. Reuses the eyebrow's negative-margin trick (cancel the
     hero's asymmetric L/R padding, re-apply symmetric padding) so it centers on
     the true page width, on the SAME axis as the eyebrow above it — not drifting
     right. */
  .lhero__listed--center {
    justify-content: center;
    align-self: stretch;
    margin: clamp(2px, 0.8vh, 8px) calc(-1 * clamp(24px, 4vw, 72px)) clamp(6px, 1.4vh, 16px) calc(-1 * clamp(40px, 7vw, 140px));
    padding: 0 clamp(24px, 4vw, 72px);
    position: relative;
    z-index: 1;
  }
  .lhero__listed-label {
    font-family: var(--f-mono); font-size: 10.5px; letter-spacing: 0.16em;
    text-transform: uppercase; color: rgba(244,239,226,0.5); white-space: nowrap;
  }
  .lhero__listed-logos { display: flex; align-items: center; gap: 26px; flex-wrap: wrap; }
  /* Desktop: "Listed on" stays on the same line as the three marks, and the award still sits on the
     page's centre line. Five columns: label | Capterra | award | Product Hunt | an invisible copy of the
     label, so both sides weigh the same; the two side marks get one shared minimum width. */
  .lhero__listed--center { display: grid; grid-template-columns: 1fr auto auto auto 1fr; align-items: center; column-gap: 26px; }
  .lhero__listed--center .lhero__listed-logos { display: contents; }
  .lhero__listed--center .lhero__listed-label { justify-self: end; }
  .lhero__listed--center::after {
    content: 'Listed on'; visibility: hidden; justify-self: start; white-space: nowrap;
    font-family: var(--f-mono); font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase;
  }
  .lhero__listed--center .lhero__listed-link { min-width: 112px; }
  .lhero__listed--center .lhero__listed-logos > :first-child { justify-content: flex-end; }
  .lhero__listed--center .lhero__listed-logos > :last-child { justify-content: flex-start; }
  /* Capterra + Product Hunt now sit BARE on the dark ground (no pill), rendered
     as flat parchment marks at one shared tone/opacity so they read as a matched
     muted pair — the quiet "listed on" treatment. */
  .lhero__listed-link {
    display: inline-flex; align-items: center; line-height: 0; text-decoration: none;
    opacity: 0.6; transition: opacity 0.16s ease;
  }
  .lhero__listed-link:hover { opacity: 0.9; }
  /* Fazier award badge, a hair taller than the 20px logos. Slightly knocked
     back so it doesn't outweigh the two bare marks. */
  /* The #1 Product of the Day award is the strongest proof in the row, so it's the largest mark and
     sits on the page's centre line (the label stacks above; three columns keep the middle centred
     whatever the side logos' widths). */
  .lhero__listed-award { display: inline-flex; align-items: center; line-height: 0; opacity: 1; transition: transform 150ms ease, opacity 0.16s ease; }
  .lhero__listed-award:hover { transform: translateY(-1px); opacity: 1; }
  /* Fazier's dark badge is white line art on a transparent ground, so it sits
     bare on the hero like the two wordmarks: no card, radius, or shadow. */
  .lhero__listed-award img {
    height: 50px; width: auto; display: block;
    background: transparent; border: 0; box-shadow: none;
  }
  /* The two awards travel as one pair in the row's middle column. */
  .lhero__listed-awards { display: inline-flex; align-items: center; gap: 14px; }
  /* PeerPush's brass-medal mark shares Fazier's 50px height. */
  .lhero__listed-award--pp img { height: 50px; }
  /* Capterra: raster wordmark (transparent PNG) painted as a flat parchment mark
     via its own alpha channel as a CSS mask — pixel-exact letterforms, exact
     brand hex, no hue artifacts, no new asset. Explicit size reserves the row. */
  .lhero__listed-cap {
    display: block; width: 87px; height: 20px;
    background-color: #F4EFE2;
    -webkit-mask: url(/capterra-logo.webp) center / contain no-repeat;
    mask: url(/capterra-logo.webp) center / contain no-repeat;
  }
  .lhero__listed-ph {
    display: inline-flex; align-items: center; gap: 6px;
    color: #F4EFE2; font-family: var(--f-sans); font-weight: 600; font-size: 14px;
    letter-spacing: -0.01em; white-space: nowrap;
  }
  /* whole PH mark (glyph + word) monochrome parchment via currentColor — the
     lone orange dot would be the loudest pixel in the hero and break the pair. */
  .lhero__listed-ph svg { width: 18px; height: 18px; flex: none; display: block; color: currentColor; }
  /* Full rolling "Featured on" marquee, relocated to the closing sign-off band
     (under the "Spark to business" tagline). Sits on the same dark ground, so
     it keeps the on-dark badge treatment; a hairline above sets it apart from
     the tagline. */
  .lclose__badges {
    /* Full-bleed: span the ENTIRE page width, edge to edge. The parent sign-off
       column is display:flex / align-items:center, so a child wider than the
       column is automatically centered on the viewport and overflows both sides
       equally — a plain width:100vw is all that's needed (adding a negative
       margin on top would double-shift it off-center). Safe from a horizontal
       scrollbar because html/body/#root clip overflow-x (global.css). The
       viewport's edge-fade mask keeps badges from butting hard against the
       screen edges. */
    width: 100vw;
    max-width: 100vw;
    margin: 28px 0 0;
    padding-top: 26px;
    border-top: 1px solid rgba(244, 239, 226, 0.12);
  }
  .lclose__badges .listedon { margin-top: 0; gap: 10px; }
  .lclose__badges .listedon__eyebrow {
    display: block;
    font-size: 10px;
    letter-spacing: 0.22em;
    color: var(--text-on-dark-muted);
  }
  .lclose__badges .listedon__viewport { max-width: 100%; }
  .lclose__badges .listedon__badge { padding: 4px 8px; border-radius: 6px; }
  .lclose__badges .listedon__badge img { height: 22px; max-width: 180px; }
  .lclose__badges .listedon__group { gap: 20px; padding-right: 20px; }
  /* Tester-quote rotator relocated into the closing CTA. A brass hairline above
     sets it apart as the final social-proof beat before the CTA button. */
  .closing-testimonials {
    margin: 8px auto 40px;
    padding-top: 28px;
    border-top: 1px solid rgba(244, 239, 226, 0.12);
    max-width: 680px;
  }
  /* Free-first pricing cue + sample-memo link on ONE line (founder: "in one line
     to optimize space"). A single flex row under the trust line, same recessive
     mono register; wraps only if it must. Both brass links. */
  .lhero__pricing {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    font-family: var(--f-mono);
    /* Bumped 12px → 14px alongside the trust line (founder request). Phone block
       still overrides to 9px. */
    font-size: 14px;
    letter-spacing: 0.04em;
    color: rgba(244, 239, 226, 0.55);
    text-align: left;
    margin-top: 8px;
    opacity: 0;
    transform: translateY(16px);
    animation: heroRiseIn 560ms cubic-bezier(0.22, 1, 0.36, 1) 480ms forwards;
  }
  .lhero__pricing a {
    color: var(--brass-light);
    text-decoration: none;
    border-bottom: 1.5px solid color-mix(in srgb, var(--brass-light) 45%, transparent);
    padding-bottom: 1px;
    transition: border-color 140ms ease;
  }
  .lhero__pricing a:hover { border-bottom-color: var(--brass-light); }
  .lhero__pricing-sep { color: color-mix(in srgb, var(--brass-light) 50%, transparent); }
  .lhero__pricing-sample { white-space: nowrap; }
  /* Quietest interactive line in the hero — the "Try It Live" proof door on its
     own line beneath the pricing row. Rises in last so it sits at the bottom of
     the hero's visual-weight ladder. Brass-underline proof treatment, never
     green, never a button: subordinate to the real "Get my verdict" CTA. */
  .lhero__demoline {
    font-family: var(--f-mono);
    font-size: 12.5px;
    letter-spacing: 0.02em;
    color: rgba(244, 239, 226, 0.5);
    /* Docked tight under the box, above the Spark-pill row. Negative margin
       offsets the form's flex gap so it sits close to the box's bottom edge
       rather than a full row's distance away. */
    margin-top: -6px;
    line-height: 1.5;
    opacity: 0;
    transform: translateY(16px);
    animation: heroRiseIn 560ms cubic-bezier(0.22, 1, 0.36, 1) 380ms forwards;
  }
  .lhero__demoline a {
    color: var(--brass-light);
    text-decoration: none;
    border-bottom: 1.5px solid color-mix(in srgb, var(--brass-light) 45%, transparent);
    padding-bottom: 1px;
    transition: border-color 140ms ease;
  }
  .lhero__demoline a:hover { border-bottom-color: var(--brass-light); }
  /* Under-box row: the outlined Spark pill (left) + the free/no-card line
     (right, right-docked under the "Get my verdict" button so "free" reads
     against the primary action). Both on one level, tight under the composer.
     Rises in with the hero cluster. */
  .lhero__subrow {
    display: flex; align-items: center; justify-content: space-between;
    /* Small COLUMN gap is only the wrap threshold — space-between sets the real
       visual spread — so keeping it tight guarantees the pill + the (enlarged)
       free line stay on one line within the 520px form before wrapping on
       mobile. Row gap 10px is what actually shows once they wrap. */
    gap: 10px 8px; flex-wrap: wrap; margin-top: 14px;
    opacity: 0; transform: translateY(16px);
    animation: heroRiseIn 560ms cubic-bezier(0.22, 1, 0.36, 1) 440ms forwards;
  }
  /* Secondary CTA — outlined on-dark BRASS pill. Brass hue-separates it from the
     green primary, and outline-vs-fill keeps it clearly the subordinate door.
     Filled brass lightning bolt; two-tone label (muted question + solid
     parchment action). No glow/lift (brand motion rule) — bg/border shift on
     hover only. */
  .lhero__sparkpill {
    display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
    padding: 11px 18px; border: 1.5px solid #C79A4E; border-radius: 999px;
    background: rgba(199,154,78,0.12); line-height: 1;
    font-family: var(--f-sans); font-weight: 600; font-size: 14px; letter-spacing: -0.005em;
    transition: background-color 160ms ease, border-color 160ms ease;
  }
  .lhero__sparkpill svg { width: 16px; height: 16px; flex: none; color: #C79A4E; }
  .lhero__sparkpill-q { color: rgba(244,239,226,0.6); font-weight: 500; }
  .lhero__sparkpill-a { color: #F4EFE2; font-weight: 600; }
  .lhero__sparkpill:hover { background: rgba(199,154,78,0.2); border-color: #E4C07A; }
  .lhero__sparkpill:focus-visible { outline: 2px solid #4FD992; outline-offset: 2px; }
  /* mono, parchment-muted, green check — a touch larger and a little more
     present now that it carries the primary CTA's reassurance, right-docked in
     the subrow under the "Get my verdict" button. */
  .lhero__freeline {
    display: inline-flex; align-items: center; gap: 7px;
    font-family: var(--f-mono); font-size: 12.5px; letter-spacing: 0.01em;
    color: rgba(244,239,226,0.72); white-space: nowrap;
  }
  .lhero__freeline svg { width: 13px; height: 13px; flex: none; color: #4FD992; }
  @media (max-width: 767px) {
    .lhero__sparkpill { font-size: 13.5px; }
    /* On phones, stack the box foot: confidentiality line full-width, then the
       verdict button full-width below it (a better tap target than a small
       left-hugged pill after the privacy line wraps). */
    .lhero__box-foot { flex-direction: column; align-items: stretch; gap: 12px; }
    .lhero__box-privacy { max-width: none; }
    .lhero__form .btn { width: 100%; }
  }
  /* Desktop centering for the trust + pricing microlines — MUST come after their
     base rules above (which set text-align: left), because media queries add no
     specificity and later source order wins. Without this, the trust line stayed
     left-aligned instead of centering under the headline (founder flagged it). */
  @media (min-width: 1025px) {
    .lhero__trustline { text-align: center; }
    .lhero__pricing { justify-content: center; }
    /* The trust + pricing microlines follow IMMEDIATELY after the CTA button
       (founder request) — the only spacing above them is the form's own
       margin-bottom. The earlier desktop margin-top nudge (which pushed them
       down to meet the removed right-panel label line) is gone now that the
       right label is removed and the conveyor aligns to the column by height
       instead. */
  }
  /* ===== Below-fold trust ledger =====
     The fuller wax-seal confidentiality ledger, RELOCATED out of the hero so it
     no longer crowds the headline moment. It's now the first thing after the
     dark stage, on parchment — keeping the trust promise at high value without
     competing with the emotional moment above. Confidentiality is this
     audience's #1 conversion fear, so it reads as a calm, legible guarantee: a
     brass wax-seal lock anchoring three checkmarked promises + a proof link.
     Calm and archival — never an alert box (≤4% heartwood tint, ≤8% border,
     brass only in the seal + a faint top-edge inset). */
  .herotrust {
    background: var(--surface-page);
    padding: clamp(22px, 4vh, 40px) 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .herotrust__row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 14px 20px;
    background: rgba(27, 58, 46, 0.04);
    border: 1px solid rgba(27, 58, 46, 0.08);
    border-radius: 12px;
    box-shadow: inset 0 1px 0 rgba(181, 133, 60, 0.2);
  }
  .herotrust__seal {
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(181, 133, 60, 0.12);
    display: grid;
    place-items: center;
  }
  .herotrust__seal svg {
    width: 16px;
    height: 16px;
    stroke: var(--brass);
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .herotrust__claims {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  /* Heartwood checks + text (NOT brass, NOT spark) — calm confirmation; the
     single brass seal is the credential anchor. */
  .herotrust__claim {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: var(--f-sans);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.2;
    color: rgba(27, 58, 46, 0.88);
    white-space: nowrap;
  }
  .herotrust__claim svg {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    stroke: var(--heartwood);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  /* Proof link — turns three claims into "claims with receipts" (the full
     protection panel lives on /how-it-works#protection). */
  .herotrust__link {
    font-family: var(--f-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: var(--brass-deep);
    text-decoration: none;
  }
  .herotrust__link:hover { color: var(--brass-deep); text-decoration: underline; }
  @media (max-width: 520px) {
    /* On a phone, a vertical list reads even more deliberately as a checklist. */
    .herotrust__row { align-items: flex-start; }
    .herotrust__claims {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
  }

  /* Right: the deliverable cascade, directly on the dark stage — no more panel
     card, no brass frame on the whole panel (the frame moves onto the memo
     certificate card itself). Transparent + overflow:visible so the cards bleed
     off the right; the stage's own overflow:hidden clips them at the viewport
     edge (the "infinite canvas" feel). align-self:stretch fills the row so the
     cascade can center vertically. */
  .lhero__panel {
    position: relative;
    align-self: stretch;
    background: transparent;
    border: none;
    box-shadow: none;
    overflow: visible;
  }
  /* ===== Hero showcase: the pure deliverable cascade (header text removed —
     the left column's oversized headline now carries the message; the right is
     the product, shown gloriously). Centers the cascade in the stage. ===== */
  .hero-showcase {
    position: relative;
    height: 100%;
    min-height: clamp(420px, 62vh, 620px);
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  /* The conveyor + 3-line label are ONE centered group (justify-content: center
     from the base rule): the label sits directly BENEATH the conveyor, both
     vertically centered in the panel. This is what the founder wants — the
     conveyor stays put (centered) and the label rides right under it, not spread
     to the far bottom. (An earlier space-between spread them to top/bottom, which
     pushed the conveyor up; reverted.) The gap between them is the label's own
     margin-top below. */
  /* The right panel now aligns to the LEFT column by matching HEIGHT, not by a
     translateY offset. The bottom label was removed, so the showcase is just the
     conveyor, centered in the row (both columns are grid-centered on the same
     axis). The carousel height is set to ~the left column's content height
     (below), so when both are centered their TOPS and BOTTOMS line up with no
     offset. Any residual desktop nudge lives here — kept at 0 unless a
     screenshot shows drift. */
  @media (min-width: 1025px) {
    .hero-showcase { transform: translateY(0); }
  }
  .hero-showcase__header {
    flex: 0 0 auto;
    position: relative;
  }
  /* Growth headline — the full 0-to-1 arc in words (spark → invention →
     product → business). Sentient 400, deliberately below the left column's
     72px H1; the parchment-on-heartwood color context separates them so it
     never competes for the page's one true H1. The longer progression line
     wraps to 2-3 lines, giving it presence without a large type size. The
     spark-green final word "business." crowns the climb as the summit anchor.
     Fades up once on load. */
  .hero-showcase__growth {
    font-family: var(--f-serif);
    font-weight: 400;
    /* Scaled up so the headline LEADS the panel (it was smaller than the
       cards' "Go." verdict, which made the panel read understated). Now it is
       the message and the cards are its evidence. */
    font-size: clamp(28px, 2.5vw, 40px);
    line-height: 1.12;
    letter-spacing: -0.02em;
    color: var(--parchment);
    max-width: 380px;
    margin: 0;
    opacity: 0;
    transform: translateY(8px);
    animation: heroMsgIn 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  /* "business you own." — the destination the arc lands on — in spark green
     with a spark underline, the one deliberate eye-magnet accent (spark =
     momentum/destination). It sits directly above the business-case card. */
  .hero-showcase__growth-accent {
    color: var(--spark-light);
    text-decoration: underline;
    text-decoration-color: var(--spark-light);
    text-decoration-thickness: 2px;
    text-underline-offset: 6px;
  }
  @keyframes heroMsgIn {
    to { opacity: 1; transform: translateY(0); }
  }
  /* Learn-by-doing support line — calm Switzer, parchment, beneath the
     progression headline. Carries the "you become an entrepreneur by doing"
     idea without competing with the serif headline. Its own keyframe so it
     settles to a recessive 0.82 opacity (the shared heroMsgIn ends at 1). */
  .hero-showcase__learn {
    font-family: var(--f-sans);
    font-weight: 400;
    font-size: clamp(13px, 1.05vw, 15px);
    line-height: 1.45;
    color: var(--parchment);
    max-width: 330px;
    margin: 12px 0 0;
    opacity: 0;
    transform: translateY(8px);
    animation: heroLearnIn 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
    animation-delay: 90ms;
  }
  @keyframes heroLearnIn {
    to { opacity: 0.82; transform: translateY(0); }
  }
  /* Framing line — a struck brass stamp dividing the message from the proof
     cards: a short brass tick before the mono label. */
  .hero-showcase__framing {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: var(--f-mono);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--brass-light);
    margin-top: 14px;
    opacity: 0;
    animation: heroMsgIn 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
    animation-delay: 160ms;
  }
  .hero-showcase__framing::before {
    content: "";
    width: 22px;
    height: 2px;
    flex: none;
    background: var(--brass);
  }
  /* ===== 8-CARD CONTINUOUS CAROUSEL =====
     The eight deliverables cycle through a front-focus spotlight in an endless
     conveyor: each card pans IN from the lower-right, settles dead-center at
     full size for a beat, then pans OUT to the upper-left and fades — looping
     forever so every report card gets its turn in front. Pure CSS: one shared
     keyframe, the 8 cards staggered by negative animation-delay so they're
     evenly spread across the loop, meaning exactly one card is focused, one
     entering, and one exiting at any instant (the other five wait invisibly).
     Transform + opacity only (GPU), zero layout shift. */
  .hero-showcase__carousel {
    position: relative;
    width: 100%;
    /* Moderate height — enough for the two-lane scatter, but NOT so tall it
       pushes the bottom label out of alignment with the left column's bottom
       microline or shoves the credibility rotator below the fold (founder:
       "fit the bottom label aligned with the left bottom text, keep the rolling
       testimonial in the viewport"). The scatter is HORIZONTAL, not vertical. */
    /* TALLER — sized to ~match the left column's content height (3-line
       headline + subhead + form + 2 microlines) so, with both columns
       vertically centered in the same grid row, the conveyor's top/bottom line
       up with the left hero's headline-top and microline-bottom (founder: "top
       and bottom aligned with the left hero's top text and bottom text edge").
       Was clamp(360px, 48vh, 452px). The 62vh mid tracks the row's own
       min-height (62vh). Fine-tune against a desktop screenshot. */
    height: clamp(440px, 62vh, 600px);
  }
  /* Cards are positioned entirely by JS (the TWO-LANE CONVEYOR in
     HeroShowcase.tsx sets transform/opacity/filter/z per frame). Base state:
     absolute, top-left origin, hidden — JS reveals + places each card. The
     FALLBACK (below 1025px, or no-JS / reduced-motion when JS clears inline
     styles) shows just the first card (market) centered. */
  .hero-cardpos {
    position: absolute;
    top: 0;
    left: 0;
    /* Smaller again so the band spreads across MORE horizontal width without the
       cards overlapping into a stack (founder: "spread out even more"). */
    /* LARGER card (founder: "proportionally increase the memo cards size").
       Was clamp(196px, 14.5vw, 248px). Widened so the bigger conveyor reads as
       bigger cards, not just more empty stage. */
    width: clamp(224px, 16.5vw, 288px);
    opacity: 0;
    will-change: transform, opacity;
  }
  /* Fallback single card, centered (tablet/phone show only the focus card; the
     desktop conveyor overrides these inline). */
  .hero-cardpos:first-child {
    opacity: 1;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .hero-card {
    background: var(--surface-card);
    border-radius: 12px;
    padding: 18px 20px;
    /* Fixed height so every card fills the SAME focus box as it cycles through
       the spotlight. Flex column so the signature FIGURE (.hero-fig, last child)
       flex-grows to FILL the lower half — no dead space below the header. */
    /* TALLER card, proportional to the wider width above (founder: "proportionally
       increase the memo cards size"). Was clamp(272px, 40vh, 316px). Keeps the
       ~0.78 width:height ratio so the certificate proportions hold, and fills more
       of the taller conveyor so the cascade spans the column top-to-bottom. */
    height: clamp(312px, 46vh, 368px);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    /* Deeper, warmer stacked shadow so the light cards lift confidently off
       the dark stage (more separation = more visual punch). */
    box-shadow: 0 2px 8px rgba(0,0,0,0.22), 0 22px 48px rgba(0,0,0,0.34);
    /* Motion lives on .hero-cardpos (the carousel conveyor); the card is static. */
  }
  /* ===== Signature FIGURE — the "figure in a dossier" that fills each card's
     lower half so no card has empty space. A hairline top-divider, a mono
     "FIG. N" caption, then the diagram in a faint-sage window that flex-grows to
     fill. Each figure spends its color budget on ONE accent (brass or spark),
     keeping eight dense cards from reading as noise. ===== */
  .hero-fig {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--border-hairline);
  }
  .hero-fig__cap {
    flex: none;
    font-family: var(--f-mono);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--brass-deep);
    margin-bottom: 7px;
  }
  .hero-fig__win {
    flex: 1 1 auto;
    min-height: 0;
    background: color-mix(in srgb, var(--heartwood) 6%, var(--surface-page));
    border: 1px solid var(--border-hairline);
    border-radius: 8px;
    padding: 10px 12px;
    overflow: hidden;
  }
  /* FIG.1 — TAM/SAM/SOM bullseye (accent: brass = SOM, the reachable core). */
  .hero-fig__bull { display: flex; align-items: center; gap: 14px; }
  .hero-fig__rings { position: relative; width: 64px; height: 64px; flex: none; }
  .hero-fig__rings span {
    position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
    border-radius: 50%;
  }
  .hero-fig__rings .r-tam { width: 64px; height: 64px; background: color-mix(in srgb, var(--heartwood) 16%, transparent); }
  .hero-fig__rings .r-sam { width: 42px; height: 42px; background: color-mix(in srgb, var(--heartwood) 34%, transparent); }
  .hero-fig__rings .r-som { width: 20px; height: 20px; background: var(--brass); }
  .hero-fig__legend { display: flex; flex-direction: column; gap: 5px; }
  .hero-fig__legend div {
    display: flex; align-items: center; gap: 7px;
    font-family: var(--f-mono); font-size: 11px; color: var(--ink);
  }
  .hero-fig__legend i { width: 8px; height: 8px; border-radius: 50%; flex: none; }
  .hero-fig__legend .k-tam { background: color-mix(in srgb, var(--heartwood) 30%, transparent); }
  .hero-fig__legend .k-sam { background: color-mix(in srgb, var(--heartwood) 55%, transparent); }
  .hero-fig__legend .k-som { background: var(--brass); }
  /* FIG.2 — field interviews: Q bubble + A bubble + tally (accent: brass Q). */
  .hero-fig__qa { display: flex; flex-direction: column; gap: 7px; }
  .hero-fig__qa .qa-q {
    align-self: flex-start;
    font-family: var(--f-sans); font-size: 11.5px; color: var(--ink);
    border: 1px solid color-mix(in srgb, var(--brass) 55%, transparent);
    border-radius: 10px 10px 10px 2px; padding: 5px 9px;
  }
  .hero-fig__qa .qa-a {
    align-self: flex-end;
    font-family: var(--f-serif); font-style: italic; font-size: 11.5px;
    color: var(--parchment); background: var(--heartwood);
    border-radius: 10px 10px 2px 10px; padding: 5px 9px;
  }
  .hero-fig__qa .qa-tally {
    display: flex; align-items: center; gap: 7px; margin-top: auto;
    font-family: var(--f-mono); font-size: 10px; color: rgba(15, 23, 20, 0.6);
  }
  .hero-fig__qa .qa-tally b {
    flex: none; width: 44px; height: 8px;
    background: repeating-linear-gradient(90deg, var(--heartwood) 0 2px, transparent 2px 5px);
    opacity: 0.6;
  }
  /* FIG.3 — patentability checks (accent: none — the brass seal above is the
     star; the checks stay calm heartwood so the memo card isn't over-brassed). */
  .hero-fig__checks { display: flex; flex-direction: column; gap: 6px; }
  .hero-fig__checks > div {
    display: flex; align-items: center; gap: 8px;
    font-family: var(--f-sans); font-size: 12.5px; color: var(--ink);
  }
  .hero-fig__checks svg { width: 15px; height: 15px; flex: none; stroke: var(--heartwood); stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
  .hero-fig__checks .checks-note {
    margin-top: auto; font-family: var(--f-mono); font-size: 10px;
    color: rgba(15, 23, 20, 0.55); display: block;
  }
  /* FIG.4 — prior-art landscape scatter (accent: spark = your idea, in the gap). */
  .hero-fig__scatter { position: relative; }
  .hero-fig__scatter .pa-dot {
    position: absolute; width: 7px; height: 7px; border-radius: 50%;
    background: var(--heartwood); opacity: 0.5;
  }
  .hero-fig__scatter .pa-gap {
    position: absolute; width: 46px; height: 46px; border-radius: 50%;
    border: 1.5px dashed color-mix(in srgb, var(--heartwood) 45%, transparent);
    transform: translate(50%, 50%);
  }
  .hero-fig__scatter .pa-mine {
    position: absolute; width: 12px; height: 12px; border-radius: 50%;
    background: var(--spark);
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--spark) 20%, transparent);
    transform: translate(50%, 50%);
  }
  .hero-fig__scatter .pa-mine-label {
    position: absolute; right: 8%; bottom: 6%;
    font-family: var(--f-mono); font-size: 9px; letter-spacing: 0.06em;
    color: var(--spark-deep);
  }
  /* FIG.5 — exploded line-art view (monochrome heartwood linework). */
  .hero-fig__exploded { display: flex; flex-direction: column; }
  .hero-fig__exploded svg { flex: 1; width: 100%; min-height: 0; stroke: var(--heartwood); stroke-width: 1.4; }
  .hero-fig__exploded .ex-n { font-family: var(--f-mono); font-size: 7px; fill: var(--heartwood); stroke: none; text-anchor: middle; font-weight: 700; }
  .hero-fig__exploded .ex-cap { flex: none; font-family: var(--f-mono); font-size: 10px; color: rgba(15, 23, 20, 0.6); margin-top: 4px; }
  /* FIG.6 — EBITDA chart (accent: brass = breakeven year; spark trend line). */
  .hero-fig__ebitda { display: flex; flex-direction: column; }
  .hero-fig__ebitda svg { flex: 1; width: 100%; min-height: 0; }
  .hero-fig__ebitda .bar { fill: var(--heartwood); opacity: 0.85; }
  .hero-fig__ebitda .bar--be { fill: var(--brass); opacity: 1; }
  .hero-fig__ebitda .be-line { stroke: var(--brass); stroke-width: 1; stroke-dasharray: 3 3; opacity: 0.7; }
  .hero-fig__ebitda .trend { fill: none; stroke: var(--spark); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  .hero-fig__ebitda .ebitda-axis { flex: none; display: flex; justify-content: space-between; margin-top: 4px; padding: 0 4px; }
  .hero-fig__ebitda .ebitda-axis span { font-family: var(--f-mono); font-size: 8px; color: rgba(15, 23, 20, 0.5); }
  /* FIG.7 — the canvas: a 3×3 poster that fills the window (accent: brass = the
     value-proposition center cell). */
  .hero-fig__canvas { display: grid; grid-template-columns: repeat(3, 1fr); grid-template-rows: repeat(3, 1fr); gap: 4px; }
  .hero-fig__canvas i { background: color-mix(in srgb, var(--heartwood) 9%, transparent); border-radius: 3px; display: block; }
  .hero-fig__canvas .c-value { background: color-mix(in srgb, var(--brass) 34%, transparent); }
  /* FIG.8 — attorney shortlist grid + match meter (accent: brass = matched). */
  .hero-fig__shortlist { display: flex; flex-direction: column; gap: 8px; }
  .hero-fig__shortlist .sl-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 5px; }
  .hero-fig__shortlist .sl-grid i { height: 14px; border-radius: 3px; background: color-mix(in srgb, var(--heartwood) 14%, transparent); }
  .hero-fig__shortlist .sl-grid .sl-on { background: var(--brass); }
  .hero-fig__shortlist .sl-meter { height: 6px; border-radius: 3px; background: color-mix(in srgb, var(--heartwood) 12%, transparent); overflow: hidden; }
  .hero-fig__shortlist .sl-meter span { display: block; height: 100%; width: 66%; background: var(--brass); border-radius: 3px; }
  .hero-fig__shortlist .sl-label { margin-top: auto; font-family: var(--f-mono); font-size: 10px; color: rgba(15, 23, 20, 0.6); }
  /* In the carousel the wax seal is always present (no one-time stamp/ring). */
  .hero-showcase__carousel .hero-card__seal { animation: none; opacity: 1; }
  .hero-showcase__carousel .hero-card__seal::after { display: none; }

  /* Card header row: brass step tag (the journey index) on the left, ink doc
     id (the registry serial) on the right — two distinct mono roles, kept
     apart by color + position so they never read as duplicates. */
  .hero-card__top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
  }
  .hero-card__step {
    font-family: var(--f-mono);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--brass-deep);
    white-space: nowrap;
  }
  .hero-card__id {
    font-family: var(--f-mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    color: rgba(15, 23, 20, 0.55);
  }
  .hero-card__title {
    font-family: var(--f-serif);
    font-size: 15px;
    font-weight: 500;
    color: var(--ink);
    margin-top: 4px;
  }
  /* Memo (middle) — the Go-verdict card; position:relative anchors the seal.
     The burnished-brass CERTIFICATE FRAME moves here from the old whole-panel
     border: this is the card with the wax seal, so it's literally the
     certificate and earns the single justified brass frame on the stage. */
  .hero-card--memo {
    padding: 20px 22px;
    position: relative;
    border: 3px solid var(--brass);
  }
  .hero-card__verdict {
    font-family: var(--f-serif);
    font-size: 40px;
    line-height: 1.05;
    letter-spacing: -0.01em;
    color: var(--heartwood); /* Go = Heartwood (verdict system frozen) */
    margin-top: 8px;
  }
  .hero-card__sub {
    font-family: var(--f-sans);
    font-size: 12px;
    color: rgba(15, 23, 20, 0.66);
    margin-top: 4px;
  }
  .hero-card__seal {
    position: absolute;
    top: 16px;
    right: 18px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--brass);
    color: var(--parchment);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--f-mono);
    font-size: 8px;
    letter-spacing: 0.06em;
    /* THE WOW BEAT: after the three cards settle (~1.3s) the seal STAMPS onto
       the certificate — a scale punch (1.18 → 1) with a soft brass ring that
       expands and fades (::after). The emotional exclamation point, landed on
       the product itself. Reduced-motion shows it resting in place. */
    opacity: 0;
    animation: heroSealStamp 440ms cubic-bezier(0.34, 1.4, 0.64, 1) 1300ms forwards;
  }
  @keyframes heroSealStamp {
    0% { opacity: 0; transform: scale(1.18); }
    35% { opacity: 1; }
    100% { opacity: 1; transform: scale(1); }
  }
  .hero-card__seal::after {
    content: "";
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    border: 2px solid var(--brass);
    opacity: 0;
    animation: heroSealRing 640ms ease-out 1320ms forwards;
  }
  @keyframes heroSealRing {
    0% { opacity: 0.55; transform: scale(0.82); }
    100% { opacity: 0; transform: scale(1.75); }
  }
  /* Case (middle) — the financial bar chart. */
  .hero-card__bars {
    display: flex;
    align-items: flex-end;
    gap: 7px;
    height: 44px;
    margin-top: 10px;
  }
  .hero-card__bars i {
    width: 18px;
    background: var(--heartwood);
    border-radius: 2px 2px 0 0;
    display: block;
  }
  .hero-card__fig {
    font-family: var(--f-mono);
    font-size: 11px;
    color: var(--ink);
    margin-top: 9px;
  }
  /* Market-viability (front) — the "Promising" verdict pill + market sizing.
     Promising uses the Heartwood dot/label (verdict system frozen — NOT spark
     green; spark stays on the headline's "business." only). */
  .hero-card__pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 11px;
    font-family: var(--f-mono);
    font-size: 11px;
    font-weight: 500;
    color: var(--heartwood);
    background: rgba(27, 58, 46, 0.1);
    border-radius: 5px;
    padding: 4px 9px;
  }
  .hero-card__pill-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--heartwood);
  }
  /* TAM / SAM / SOM market-sizing stack — real numbers, mono, with small
     tracked labels above each figure. */
  .hero-card__market {
    display: flex;
    gap: 18px;
    margin-top: 12px;
  }
  .hero-card__market > div {
    font-family: var(--f-mono);
    font-size: 12px;
    font-weight: 500;
    color: var(--ink);
  }
  .hero-card__market span {
    display: block;
    font-size: 8px;
    letter-spacing: 0.1em;
    color: rgba(15, 23, 20, 0.5);
    margin-bottom: 2px;
  }
  /* Market card is the front beauty shot — position:relative anchors the spark
     down-arrow "start here" cue at its top-right corner. */
  .hero-card--market { position: relative; }
  .hero-card__arrow {
    position: absolute;
    top: 12px;
    right: 14px;
    line-height: 0;
  }
  .hero-card__arrow svg {
    width: 20px;
    height: 20px;
    stroke: var(--spark);
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  /* ===== Specimen types for the 5 non-original carousel cards ===== */
  /* Customer discovery kit — interview prompts on a brass rule. */
  .hero-card__qlist { margin-top: 14px; display: flex; flex-direction: column; gap: 8px; }
  .hero-card__qlist div {
    font-family: var(--f-serif);
    font-size: 13px;
    color: rgba(15, 23, 20, 0.82);
    padding-left: 12px;
    border-left: 2px solid color-mix(in srgb, var(--brass) 50%, transparent);
    line-height: 1.3;
  }
  /* Prior-art search — a Boolean query line + CPC classification chips. */
  .hero-card__query {
    margin-top: 14px;
    font-family: var(--f-mono);
    font-size: 12px;
    color: var(--ink);
    background: rgba(27, 58, 46, 0.05);
    border-radius: 6px;
    padding: 8px 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hero-card__chips { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
  .hero-card__chips span {
    font-family: var(--f-mono);
    font-size: 11px;
    color: var(--brass-deep);
    background: rgba(181, 133, 60, 0.1);
    border-radius: 5px;
    padding: 3px 8px;
  }
  /* Design breakdown — numbered component rows. */
  .hero-card__rows { margin-top: 14px; display: flex; flex-direction: column; gap: 9px; }
  .hero-card__rows > div {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--f-sans);
    font-size: 13px;
    color: var(--ink);
  }
  .hero-card__rows b {
    font-family: var(--f-mono);
    font-size: 11px;
    color: var(--parchment);
    background: var(--heartwood);
    border-radius: 4px;
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
  }
  .hero-card__rows span {
    font-family: var(--f-mono);
    font-size: 11px;
    color: rgba(15, 23, 20, 0.5);
    margin-left: auto;
  }
  /* Business model canvas — a mini 3×3 block grid, the value-prop cell accented. */
  .hero-card__grid9 {
    margin-top: 14px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 30px;
    gap: 4px;
  }
  .hero-card__grid9 i { background: rgba(27, 58, 46, 0.07); border-radius: 4px; display: block; }
  .hero-card__grid9 i:nth-child(5) { background: rgba(31, 157, 99, 0.16); }
  /* Attorney match — an anonymized profile row (icon avatar, never a photo). */
  .hero-card__profile { margin-top: 14px; display: flex; align-items: center; gap: 12px; }
  .hero-card__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(27, 58, 46, 0.08);
    display: grid;
    place-items: center;
    flex: none;
  }
  .hero-card__avatar svg { width: 22px; height: 22px; stroke: var(--heartwood); stroke-width: 1.6; fill: none; }
  .hero-card__profile-role { font-family: var(--f-serif); font-size: 14px; color: var(--ink); }
  .hero-card__profile-meta { font-family: var(--f-mono); font-size: 11px; color: rgba(15, 23, 20, 0.55); margin-top: 2px; }
  /* Brass pill variant (Mom Test, USPTO registered) vs the heartwood default. */
  .hero-card__pill--brass { color: var(--brass-deep); background: rgba(181, 133, 60, 0.12); }
  .hero-card__pill--brass .hero-card__pill-dot { background: var(--brass); }
  /* Entrance keyframes shared by the left column (rise) and the recessive
     subhead (rises to 0.82, not 1). The headline lines have their own wipe. */
  @keyframes heroRiseIn { to { opacity: 1; transform: none; } }
  @keyframes heroSubIn { to { opacity: 0.9; transform: none; } }
  @keyframes heroHeadlineWipe {
    0% { -webkit-mask-position: 100% 0; mask-position: 100% 0; opacity: 0; }
    15% { opacity: 1; }
    100% { -webkit-mask-position: 0 0; mask-position: 0 0; opacity: 1; }
  }
  /* Reduced motion: everything rests in its final position, no entrance,
     no wipe, no stamp, no ring, no depth-pool fade. */
  @media (prefers-reduced-motion: reduce) {
    .lhero::before { animation: none; opacity: 1; }
    .lhero__eyebrow, .lhero__form, .lhero__listed, .lhero__subrow, .lhero__pricing, .lhero__secondary,
    .hero-card, .hero-card__seal {
      opacity: 1;
      transform: none;
      animation: none;
    }
    .lhero__sub { opacity: 0.82; transform: none; animation: none; }
    .lhero__headline-line {
      opacity: 1;
      -webkit-mask-image: none;
      mask-image: none;
      animation: none;
    }
    .hero-card__seal::after { animation: none; opacity: 0; }
  }
  /* Credibility strip — outer wrapper stays a quiet hairline strip; the
     rotator inside crossfades one slide at a time (see CredibilityRotator). */
  /* Credibility rotator — a quiet centered strip below the unified hero card,
     inside the hero viewport (the hero is sized so the card + this strip both
     sit above the fold). No border of its own; reads as a calm footnote. */
  .lhero__strip {
    flex: 0 0 auto;
    padding: clamp(6px, 0.9vh, 12px) 0 clamp(4px, 0.6vh, 8px);
  }
  /* All slides share one grid cell so the strip never changes height
     between transitions (zero layout shift), sizing to the tallest slide. */
  .cred-rotator {
    max-width: 1180px;
    margin: 0 auto;
    display: grid;
    place-items: center;
    min-height: 48px;
    text-align: center;
  }
  .cred-slide {
    grid-area: 1 / 1;
    opacity: 0;
    transition: opacity 500ms cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: none;
    max-width: 66ch;
  }
  .cred-slide[data-active="true"] { opacity: 1; pointer-events: auto; }
  /* Value props: recessive mono boilerplate. */
  /* The rotator sits on the green hero field, so its text is Parchment-based:
     recessive value props at lower opacity, tester pull-quotes at full strength. */
  .cred-props {
    font-family: var(--f-mono);
    font-size: 14px;
    letter-spacing: 0.04em;
    color: rgba(244,239,226,0.72);
    white-space: nowrap;
  }
  .cred-props strong { color: var(--parchment); font-weight: 600; }
  .cred-statement {
    font-family: var(--f-mono);
    font-size: 14px;
    letter-spacing: 0.03em;
    line-height: 1.5;
    color: rgba(244,239,226,0.8);
  }
  /* Tester voices: editorial serif-italic pull-quote, medium weight at full
     Parchment strength so the thin italic pops cleanly against the green —
     the brand's preferred way to carry credibility. */
  .cred-quote {
    font-family: var(--f-serif);
    font-style: italic;
    font-weight: 500;
    font-size: 19px;
    line-height: 1.35;
    letter-spacing: -0.01em;
    color: var(--parchment);
  }
  .cred-cite {
    font-family: var(--f-mono);
    font-size: 12px;
    letter-spacing: 0.04em;
    /* At rest 0.64 was 5.38:1 on heartwood — passing. But the rotator
       crossfades slides via opacity, so a slide sampled MID-FADE dips below
       4.5:1 and trips Lighthouse's contrast audit intermittently (the "dropped
       again" desktop flag). 0.80 is 7.5:1 at rest, leaving enough headroom that
       a partial-opacity crossfade frame still clears AA. Matches
       .cred-statement's 0.80; the cite reads recessive from its 12px mono size,
       not from a lower opacity. */
    color: rgba(244,239,226,0.80);
    margin-left: 10px;
    white-space: nowrap;
  }
  .lhero__dot { color: var(--parchment); opacity: 0.4; }
  @media (max-width: 600px) {
    .cred-props { font-size: 12.5px; white-space: normal; }
    .cred-quote { font-size: 16px; }
    .cred-statement { font-size: 12.5px; }
    .cred-cite { display: block; margin-left: 0; margin-top: 6px; }
    .cred-rotator { min-height: 74px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .cred-slide { transition: none; }
  }

  /* Stack content-first below ~900px: form column on top (stays above the
     fold), then the dark panel as a full-width block, then the credibility
     floor. The panel gets an explicit aspect-ratio so its height is reserved
     before the image loads (no layout shift). */
  @media (max-width: 1100px) {
    /* Bottom padding matches the desktop rule so the hero's last line clears
       the viewport break in the 1025–1100 two-column range too. */
    .lhero { padding: 16px 28px clamp(24px, 4vh, 48px); }
  }
  /* ── Layout-stacking umbrella (≤1024): tablets AND phones both collapse the
     hero to a single column. The two-column split needs ~1000px+; below that
     the showcase leaves its absolute-positioned diagonal for normal flow.
     STRUCTURE only — the tablet and phone blocks below own type, padding, and
     card arrangement. */
  @media (max-width: 1024px) {
    .lhero { min-height: 0; display: block; padding: 16px 28px 0; }
    .lhero__split { grid-template-columns: 1fr; min-height: 0; gap: clamp(16px, 2.2vw, 28px); }
    .lhero__panel { min-height: 0; }
    /* The carousel is self-contained (absolute cards in a fixed-height relative
       box), so it runs at every width; no per-card stacking needed. Just cap its
       height so the stacked panel isn't too tall below the content. Floor raised
       to 400px to comfortably clear the now-taller base card (368px) when it's
       centered in the tablet single-lane spotlight. (Phones override this again
       below.) */
    .hero-showcase__carousel { height: clamp(400px, 50vh, 460px); }
  }

  /* ── TABLET PORTRAIT (768–1024): the deliberate iPad register. Single column
     but WIDENED (620px measure) and UPSCALED (headline to ~76px) so it reads as
     designed-for-tablet, not a blown-up phone. The full subhead is restored
     (the phone hides it), and the three deliverable cards form a 3-across
     advisory-board lineup instead of a 1-up stack. Must come AFTER the ≤1024
     umbrella in source order so its overrides win (media queries add no
     specificity). */
  @media (min-width: 768px) and (max-width: 1024px) {
    /* iPad portrait = SINGLE COLUMN with a FULL-BLEED animated conveyor band.
       The two-column version fit everything but starved the conveyor: it's a
       HORIZONTAL 8-card animation that needs WIDTH, and a narrow right rail
       only showed ~1 card with imperceptible motion. iPad portrait's abundant
       axis is HEIGHT and its scarce axis is WIDTH, so the conveyor gets the
       full width here and the stack flows top to bottom:
         eyebrow → content → full-width conveyor band → testimonial.
       Layout: eyebrow → content → full-width conveyor band → testimonial, in a
       flex column, sized NATURALLY (not locked to 100dvh).

       WHY NOT height:100dvh: this hero is NOT at the top of the page — a promo
       bar (~38px) + the sticky 100px nav sit above it, so a 100dvh hero starts
       ~138px down and its bottom 138px (exactly where the conveyor band lives)
       falls below the fold and gets clipped by overflow:hidden — the band showed
       "half covered" on iPad portrait. Sizing the hero to its content instead,
       with a definite-height band below (see .lhero__panel), keeps the whole
       band on screen; the page simply scrolls to it like the rest of the landing
       rather than cutting it off inside a one-viewport box. overflow is left
       visible vertically so nothing clips; the band's own overflow:hidden + the
       global overflow-x clip still contain the full-bleed conveyor horizontally. */
    .lhero {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      /* Tightened top padding + gaps recover vertical space under the nav so the
         content stack + conveyor band both land in the fold (design expert). Top
         padding + column gap trimmed a second time (paired with the band shrink
         below) to reclaim ~110px total at 768×1024 so the WHOLE conveyor band —
         full card height plus drift room — seats above the fold at the tightest
         iPad portrait (classic iPad / mini). This is also the flex COLUMN gap that
         sets the eyebrow→headline→sub→composer rhythm, since .lhero__split is
         display:contents here. */
      padding: clamp(12px, 1.5vh, 22px) clamp(28px, 5vw, 56px) clamp(24px, 3.5vh, 44px);
      gap: clamp(8px, 1.1vh, 14px);
    }
    /* display:contents dissolves the split wrapper so content + panel join the
       hero's own flex flow. DOM order is eyebrow → content → panel → strip,
       which is exactly the stack we want — so the eyebrow lands at the TOP
       naturally (the two-column grid had auto-placed it to the bottom). */
    .lhero__split { display: contents; }
    .lhero__eyebrow--top { flex: 0 0 auto; margin: 0; }
    .lhero__content { flex: 0 0 auto; align-items: center; padding: 0; }
    .lhero__main { max-width: 640px; }
    .lhero__panel {
      /* DEFINITE height (was flex:1 filling a 100dvh hero). The hero is now
         natural-height, so the band needs its own height rather than absorbing
         leftover space — this is also what guarantees the conveyor is never
         squeezed below the card height and clipped. Comfortably taller than the
         card (clamp(196px,22vh,300px)) so the spotlight card has room to drift. */
      flex: 0 0 auto;
      /* Shrunk from clamp(300px,30vh,340px): the band is the dominant vertical
         cost in the hero, and it's what pushed its own bottom (and the bottom of
         the drifting cards) past the 768×1024 fold. This reclaims ~81px of page
         height. The cards live INSIDE the band so shrinking THEM reclaims nothing
         — only the band height counts toward the flow; the card trim below exists
         solely to preserve drift room inside the shorter band. Invariant to hold
         if this is ever retuned: band height must stay >= card height + ~50px so
         the cards never clip inside the band or trigger an internal scrollbar. */
      /* Cap LOWERED (was clamp(222px,22.1vh,296px)). On the tall 12.9" iPad
         portrait (1024×1366) the old 296px cap put the band's bottom near
         y≈1180 in layout coords; once the browser chrome (tab + address bar)
         and the launch promo bar shrink the *visible* fold, that bottom fell
         below it and the band read as clipped. A shorter band pulls the whole
         stack up so it clears the fold on both the 12.9" and the shorter
         768×1024 iPad. Invariant still holds: band height >= card height + ~50px
         (card cap 184 below), so cards never clip inside the band. */
      height: clamp(200px, 19vh, 236px);
      min-height: 0;
      position: relative;
      overflow: hidden;
      /* full-bleed past the hero's horizontal padding so the horizontal
         conveyor has the whole viewport width to show 2-3 drifting cards */
      width: 100vw;
      margin-left: calc(50% - 50vw);
      margin-top: clamp(6px, 1vh, 14px);
    }
    .lhero__strip { flex: 0 0 auto; margin: 0; }

    /* Enlarged hero, tuned so content + band fit the fold (design expert). */
    .lhero__eyebrow { font-size: clamp(14px, 1.7vw, 18px); letter-spacing: 0.1em; margin-bottom: 8px; }
    .lhero__eyebrow::before { width: 44px; margin-bottom: 9px; }
    .lhero__headline {
      /* Trimmed from clamp(60px,9vw,92px): the single biggest vertical lever.
         Still emphatically tablet-scale (bigger than the 60px phone ceiling), so
         it never reads as a blown-up phone, while shaving ~35–46px off the
         3-line stack so the conveyor band lands in the fold. */
      /* Nudged down from clamp(54px,7.4vw,78px) as part of the in-fold reclaim.
         A ~5px/line trim reads as a COMPOSED tablet (scaled-down desktop), not a
         blown-up phone — and pairs with the band shrink so the conveyor seats in
         the fold without gutting the readable subhead below. */
      font-size: clamp(44px, 5.6vw, 60px);
      line-height: 0.96;
      letter-spacing: -0.03em;
      max-width: none;
      margin-bottom: 10px;
    }
    .lhero__sub {
      display: block !important;
      font-size: clamp(15px, 1.7vw, 19px) !important;
      line-height: 1.4 !important;
      /* 46ch (was 52) forces a clean ~3-line wrap and saves a line of height. */
      max-width: 46ch;
      margin: 0 auto 10px;
    }
    .lhero__sub-mobile { display: none !important; }
    /* Pricing line is TERTIARY here (the composer's "Get my verdict" is primary,
       the Spark pill secondary) and the exact "Pay as you go, from US$9.99" line
       renders again in the very next section — so drop it on tablet portrait to
       reclaim ~50px, which is what tips 768×1024 from a sliver to a full first
       card in the fold. The Spark pill stays. */
    .lhero__pricing { display: none; }
    .lhero__form { margin-top: 4px; }
    .lhero__trustline { margin-top: 10px; }

    /* Conveyor: the band fills the panel; the card height is bounded to a slice
       of the viewport height (guaranteed smaller than the band on the tightest
       iPad), and the cardpos WIDTH follows at the card's ~0.78 w:h ratio via a
       matching vh unit — so nothing clips vertically and the full-bleed width
       shows several cards drifting. The JS conveyor reads these live dimensions
       each frame, so no JS change is needed. */
    /* Kill the desktop min-height (clamp(420px,62vh,620px)): it forced the
       showcase — and therefore the carousel, which is height:100% of it — far
       taller than the band (up to 620px at 1366 tall). The JS conveyor centers
       each card at the CAROUSEL's midpoint, so an oversized carousel dropped the
       cards ~300px below the band and under the fold. Matching the showcase to
       the band height re-centers the cards inside the visible band. */
    .hero-showcase { min-height: 0 !important; height: 100% !important; }
    .hero-showcase__carousel { height: 100% !important; }
    /* Card trimmed AGAIN to match the shorter band (clamp(222px,22.1vh,296px))
       so it keeps ~52px of vertical drift room inside it (band >= card + ~50px —
       the no-clip invariant). Width follows at the ~0.78 w:h ratio so the shorter
       cards don't read chunky. */
    .hero-card { height: clamp(150px, 14.5vh, 184px) !important; padding: 14px 16px !important; }
    .hero-cardpos { width: clamp(118px, 11.3vh, 144px) !important; }
  }
  /* ── PHONE (≤767): the HEADLINE is the star. Enlarge it, keep the eyebrow a
     tight brass kicker, CUT the full subhead (a condensed mini-subhead returns
     as small print), and stack the three deliverable cards 1-up compacted.
     Retriggered from the former 900px point down to 767 now that the tablet
     band (768–1024) owns iPad-portrait widths. Targets the real .lhero__
     classes; the landing-hero-scoped block lower in the file never matched. */
  /* Mobile + tablet: CENTER the entire hero left column on the page (founder:
     "make eyebrow, headline, trust info, cta centered on the page"). Placed after
     the base rules (which set text-align: left) so source order wins. On desktop
     the min-width:1025 block owns centering-within-the-column; this owns the
     single-column mobile/tablet layout where centering = centered on the page. */
  @media (max-width: 1024px) {
    .lhero__content { align-items: center; }
    .lhero__main { text-align: center; }
    .lhero__headline { text-align: center; }
    .lhero__sub { text-align: center; margin-left: auto; margin-right: auto; }
    .lhero__form { margin-left: auto; margin-right: auto; }
    .lhero__trustline { text-align: center; }
    .lhero__pricing { justify-content: center; }
    .lhero__secondary { justify-content: center; }
  }
  @media (max-width: 767px) {
    /* Tighter hero top padding on phones so the badge strip sits close under
       the nav (was 16px + strip flow spacing = ~83px of dead space above the
       badge row). */
    .lhero { padding: 4px 16px 0; }
    /* Pull the strip up tight under the nav. A stubborn ~60px of leading space
       inside the hero (above the badge row) resisted padding/margin zeroing, so
       a decisive negative top margin closes it directly. The section stays at
       the nav's bottom edge; this only pulls the first child up within the
       section's own top area (never under the sticky nav, which sits far above). */
    .lhero__topbadges { margin-top: -56px; padding-top: 0; padding-bottom: 8px; }
    .lhero__topbadges .listedon { gap: 4px; }
    .lhero__topbadges .listedon__eyebrow { margin: 0 0 4px; }
    /* "Listed on" hero trust row on phones — the three marks (Capterra, PeerPush,
       Product Hunt) wrapped onto multiple lines at narrow widths. Stack the label
       ABOVE and hold all three badges on a single NO-WRAP row, sized with vw-based
       clamps so they shrink together to fit even a 320px screen. Neutralize the
       centered variant's desktop negative bleed margins so nothing overflows the
       viewport. */
    .lhero__listed { flex-direction: column; align-items: center; gap: 8px; flex-wrap: nowrap; }
    .lhero__listed--center { margin-left: 0; margin-right: 0; padding-left: 0; padding-right: 0; }
    .lhero__listed-label { font-size: 10px; }
    /* Three columns, so the award sits on the page's centre line whatever the
       side logos' widths (Product Hunt is wider than Capterra, which pushed a
       plain centred row's middle badge visibly left). */
    .lhero__listed-logos { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; width: 100%; gap: clamp(12px, 4vw, 24px); }
    .lhero__listed-logos > :first-child { justify-self: end; }
    .lhero__listed-logos > :last-child { justify-self: start; }
    /* On phones Capterra is sized to the same width as the Product Hunt mark (icon plus words), so its gap
       to the left edge matches Product Hunt's gap to the right edge. Its lettering ends up a little larger
       than Product Hunt's. The width tracks the PH mark's own vw scaling and 14px cap. Height keeps the
       wordmark's 87:20 box. */
    .lhero__listed-cap { width: clamp(82px, 25.6vw, 106px); height: clamp(18.9px, 5.89vw, 24.4px); }
    .lhero__listed-award img { height: clamp(30px, 9.2vw, 42px); }
    /* Phones: the two awards get their own centred row on top, and Capterra and
       Product Hunt share the row below, mirrored around the centre line. */
    .lhero__listed-logos { grid-template-columns: 1fr 1fr; row-gap: 12px; }
    .lhero__listed-awards { grid-column: 1 / -1; justify-self: center; order: -1; gap: clamp(8px, 2.6vw, 14px); }
    .lhero__listed-award img { height: clamp(30px, 9.4vw, 40px); }
    .lhero__listed-award--pp img { height: clamp(30px, 9.4vw, 40px); }
    /* Phones: the label stacks above, and the three marks sit in their own centred grid. */
    .lhero__listed--center { display: flex; }
    .lhero__listed--center::after { display: none; }
    .lhero__listed--center .lhero__listed-link { min-width: 0; }
    /* Tighter gaps on phones so the larger award keeps its full width between the two side marks. */
    .lhero__listed--center .lhero__listed-logos { display: grid; gap: clamp(8px, 2.6vw, 14px); }
    .lhero__listed--center .lhero__listed-logos > :first-child { justify-self: end; }
    .lhero__listed--center .lhero__listed-logos > :last-child { justify-self: start; }
    .lhero__listed-award img { max-width: none; }
    .lhero__listed-ph { font-size: clamp(11px, 3.4vw, 14px); gap: 5px; }
    .lhero__listed-ph svg { width: clamp(14px, 4.2vw, 18px); height: clamp(14px, 4.2vw, 18px); }
    /* Recover measure: base side padding squeezed the headline into a ~276px
       column; 20px sides give it a tight 2-line lock. */
    .lhero__content {
      padding: 26px 20px 22px;
      justify-content: flex-start;
    }
    .lhero__eyebrow {
      font-size: 14px;
      letter-spacing: 0.1em;
      margin-bottom: 12px;
    }
    /* Drop the flanking brass ticks on phones. The eyebrow copy wraps to ~2
       lines here, and with flex-wrap the ::before / ::after ticks broke onto
       their OWN lines above and below the text — padding the banner out with
       ~60–90px of dead vertical space between the nav and the eyebrow (the gap
       the founder flagged on mobile). A clean centered line, pulled up tight
       under the nav, reads far better. */
    .lhero__eyebrow--top::before,
    .lhero__eyebrow--top::after { display: none; }
    /* Reset the eyebrow's desktop negative margins on phones (the --top variant
       cancels the hero's ASYMMETRIC desktop padding with negative margins; the
       phone .lhero padding is SYMMETRIC 16px, so those over-pull it left) and
       zero the top margin so the eyebrow sits right under the nav. */
    .lhero__eyebrow--top {
      margin-top: 2px;
      margin-left: 0;
      margin-right: 0;
      padding-left: 0;
      padding-right: 0;
      flex-wrap: wrap;
    }
    /* Eye-catchy but sized to LOCK as a clean 2-line lockup. The headline
       the 3-line stack is "Your spark" / "to a real" / "business." — three
       SHORT lines (longest "Your spark" = 10 ch), so the phone font can go BIG
       and poster-like. Bumped LARGER AGAIN (founder: "make the headline even
       bigger") from clamp(38px, 12.8vw, 58px). CEILING on the phone size: each
       line is nowrap and the hero section is overflow:hidden, so the widest
       line ("Your spark", ~6.3px per font-px) must stay under the right-edge
       clip (~354px of usable width from the x=36 left inset). At 14vw the font
       is ~54.6px on a 390px viewport → "Your spark" ≈ 344px, ~10px inside the
       clip — near the safe limit, so DON'T push vw past ~14 without re-checking
       for a clipped first line. */
    .lhero__headline {
      /* Dropped from clamp(42px, 14vw, 60px) → the headline is now Cabinet
         Grotesk, which sets WIDER than the old serif, so 14vw risked clipping
         "Your spark" against the overflow:hidden right edge. Eased again to 11vw
         because the headline is now a uniform 800 weight (was 500 setup lines),
         which sets wider still — 11vw keeps the widest line ("Your spark")
         safely inside the clip. */
      font-size: clamp(36px, 11vw, 52px);
      line-height: 1.03;
      letter-spacing: -0.035em;
      max-width: none;
      margin-bottom: 18px;
    }
    .lhero__headline-line { white-space: nowrap; }
    /* Full subheading cut on phones — the headline + CTA lead. */
    .lhero__sub { display: none; }
    /* ...but bring a condensed subhead back — bigger now (founder: "subhead can
       be bigger"), still a narrow centered measure so it clarifies without
       overtaking the hero. !important defeats the section:not(.landing-hero) p
       leak rule (the hero is classed lhero, so the :not() exclusion misses it). */
    .lhero__sub-mobile {
      display: block !important;
      font-size: 16px !important;
      line-height: 1.5 !important;
      /* Parchment, not ink — the hero stage is heartwood at every width. */
      color: var(--parchment);
      opacity: 0.9;
      /* Widened 30ch → 34ch so the sentence lands in a CLEAN 3 lines with slack.
         At 30ch it was right on the edge: on a device with display-zoom or a
         larger system font (reported on a Samsung S26 Ultra) one word spilled to
         a 4th line, orphaning "potential." The extra measure gives the last line
         room. */
      max-width: 34ch !important;
      margin: 0 auto 20px !important;
      text-align: center !important;
    }
    /* Keep "commercial potential." as ONE unbreakable unit in the mobile subhead
       so a single word ("potential.") can never orphan onto its own line — worst
       case the whole two-word phrase wraps together to the last line. */
    .lhero__sub-mobile .lhero__sub-accent { white-space: nowrap; }
    /* Trust + pricing microlines: keep the whole block to a CLEAN 2 lines —
       trust on line 1, pricing on line 2. Each PHRASE is a nowrap token (nbsp
       within the phrase, breakable spaces at the "·" separators — see markup),
       so the line only ever wraps at a separator, never mid-phrase. A small
       font + tight tracking keeps the trust line on ONE line even on large
       phones with Samsung-style display-zoom / bigger-font accessibility
       settings (which narrow the effective CSS width). If it still must wrap at
       an extreme zoom it drops the whole link to a second line cleanly. */
    .lhero__trustline {
      font-size: 9px;
      letter-spacing: 0.01em;
      line-height: 1.55;
    }
    .lhero__trustline a { white-space: nowrap; }
    .lhero__pricing {
      font-size: 9px;
      letter-spacing: 0.01em;
      flex-wrap: nowrap;
      white-space: nowrap;
      gap: 7px;
    }
    /* Panel: the conveyor runs a SINGLE-LANE mobile spotlight here (JS branches
       on width). Give the cards a phone-appropriate width (one readable card
       centered) and cap the carousel height. */
    .hero-showcase__carousel { height: clamp(320px, 46vh, 380px); }
    .hero-cardpos { width: min(300px, 84vw); }
    .hero-card { padding: 14px 16px; height: clamp(268px, 44vh, 320px); }
  }
  /* Guard the cap lockup against overflow on very narrow phones. */
  @media (max-width: 380px) {
    .lhero__brandcap-lockup {
      transform: scale(0.84);
      transform-origin: left center;
    }
  }
  /* NOTE: a stray closing brace used to sit above these two rules, orphaning
     them out of their intended media query — so the lhero__form .btn width:100%
     rule leaked to ALL widths. That was invisible while the hero CTA was a
     single full-width button, but it fought the new docked (hug-width) verdict
     button. The button rule is removed (the docked button hugs at every size);
     the radius tweak is kept at its long-standing global scope to avoid changing
     content/panel. */
  .lhero__content, .lhero__panel { border-radius: 18px; }

  /* Advisory-board band on the light page ground. It now follows the Ink
     guarantees band (not "How it works" directly), so it gets its own generous
     top padding — the diagram sits centered with breathing room instead of
     jammed against the section's top edge. Centered, capped column; the
     diagram's own gutter breakout gives it presence without overflowing. */
  .lhow-board {
    background: var(--surface-page);
    padding: clamp(64px, 8vw, 96px) 32px 88px;
    /* The diagram bleeds into the gutters (and its nodes drift); on phones that
       ran past the screen edge and made the whole page ~34px wider than the
       viewport, so it wobbled sideways. Clip at the band's own edges. */
    overflow-x: clip;
  }
  .lhow-board__inner {
    max-width: 720px;
    margin: 0 auto;
  }
  /* Give the advisory-board band a proper section TITLE, consistent with the
     page's other section headings (Sentient serif, section scale) instead of
     the compact Switzer caption the shared component uses in the /ve hero. Scoped
     to .lhow-board so /ve stays untouched. The eyebrow keeps its small mono
     label, matching every other section's eyebrow+heading pattern. */
  .lhow-board .abd-cap {
    font-family: var(--f-serif, 'Sentient'), serif;
    font-weight: 500;
    font-size: clamp(26px, 3vw, 34px);
    line-height: 1.2;
    letter-spacing: -0.01em;
    max-width: none;
    margin: 14px auto 20px;
  }
  .lhow-board .abd-cap em { font-style: italic; }
  @media (max-width: 560px) {
    .lhow-board { padding: clamp(48px, 12vw, 64px) 20px 64px; }
    .lhow-board .abd-cap { font-size: 24px; }
  }

  /* The engine — spec-sheet section. Editorial masthead (left, sticky) + a
     hairline readout ledger (right). Static/tabular/mono-dense on purpose, so it
     contrasts the 3-step "How it works" cards and the animated diagram. */
  /* Explicit parchment ground (not the var) so this section can never inherit or
     transiently resolve a dark value — it previously flashed heartwood-dark on
     desktop during fast scroll (a sticky-masthead repaint glitch that also hid
     the ledger), which read as "dead space". Hardcoding the light ground + the
     no-longer-sticky masthead below removes that failure mode entirely. */
  .lmethod { background: #f4efe2; padding: clamp(64px, 8vw, 112px) 32px; }
  .lmethod__inner {
    max-width: 1040px; margin: 0 auto;
    border-top: 1px solid rgba(15,23,20,0.12); padding-top: 44px;
    display: grid; grid-template-columns: minmax(280px, 360px) 1fr; gap: clamp(40px, 5vw, 64px);
    align-items: stretch;
  }
  /* The masthead is now a FILLED tinted panel that stretches to the full height
     of the (taller) ledger, with its content vertically centered inside. This
     kills the old dead space two ways: the empty left column is now an
     intentional heartwood-tinted "band", and there's no sticky/centered float to
     leave a transparent gap. Content-centered so short copy still sits balanced. */
  .lmethod__mast {
    align-self: stretch; display: flex; flex-direction: column; justify-content: center;
    background: linear-gradient(158deg, rgba(27,58,46,0.07) 0%, rgba(27,58,46,0.025) 100%);
    border: 1px solid rgba(27,58,46,0.09);
    border-radius: 20px;
    padding: clamp(28px, 3vw, 40px);
  }
  .lmethod__eyebrow {
    font-family: var(--f-mono); font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase;
    color: #8C6829;
  }
  .lmethod__h {
    font-family: var(--f-serif); font-weight: 400; font-size: clamp(30px, 3.5vw, 44px);
    line-height: 1.05; letter-spacing: -0.02em; color: var(--ink); margin: 16px 0 0;
  }
  /* "An orchestration engine." — highlighted in spark green so the line that
     names the whole idea stands out from "Not a chatbot." above it. */
  .lmethod__h em { font-style: italic; color: #1F9D63; }
  .lmethod__deck {
    font-family: var(--f-sans); font-size: 16px; line-height: 1.5; color: rgba(15,23,20,0.68);
    margin: 16px 0 0; max-width: 34ch;
  }
  .lmethod__stats { display: flex; flex-wrap: wrap; gap: 24px 32px; margin-top: 28px; }
  .lmethod__stat { display: flex; flex-direction: column; }
  .lmethod__stat-n {
    font-family: var(--f-serif); font-size: clamp(34px, 4vw, 46px); line-height: 1; letter-spacing: -0.01em;
    color: var(--heartwood, #1B3A2E);
  }
  .lmethod__stat-l {
    font-family: var(--f-mono); font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase;
    color: rgba(15,23,20,0.55); margin-top: 8px; max-width: 14ch; line-height: 1.4;
  }
  .lmethod__ledger { display: flex; flex-direction: column; }
  .lmethod__row { padding: 24px 0; border-top: 1px solid rgba(15,23,20,0.10); }
  .lmethod__row:first-child { border-top: none; padding-top: 0; }
  .lmethod__rhead { display: flex; gap: 12px; align-items: baseline; }
  .lmethod__idx { font-family: var(--f-mono); font-weight: 700; font-size: 13px; color: var(--brass-deep); }
  .lmethod__kick {
    font-family: var(--f-mono); font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--text-decorative);
  }
  .lmethod__lead {
    font-family: var(--f-sans); font-weight: 600; font-size: 19px; line-height: 1.3; letter-spacing: -0.01em;
    color: var(--ink); margin: 12px 0 0;
  }
  .lmethod__body {
    font-family: var(--f-sans); font-size: 14.5px; line-height: 1.55; color: rgba(15,23,20,0.66);
    margin: 9px 0 0; max-width: 52ch;
  }
  .lmethod__data {
    font-family: var(--f-mono); font-size: 11px; letter-spacing: 0.03em; color: var(--text-decorative);
    margin: 14px 0 0;
  }
  .lmethod__data b { color: var(--brass-deep); font-weight: 500; }
  /* Row 04 is the converged "output" state — full background tint (never an edge
     accent), no top rule; its index + first data token go spark green. */
  .lmethod__row--out {
    background: rgba(31,157,99,0.06); border-top: none; border-radius: 14px;
    padding: 22px; margin-top: 12px;
  }
  .lmethod__row--out .lmethod__idx { color: var(--spark-ink); }
  .lmethod__row--out .lmethod__data b { color: var(--heartwood, #1B3A2E); }
  @media (max-width: 860px) {
    .lmethod__inner { grid-template-columns: 1fr; gap: 40px; }
    .lmethod__mast { position: static; }
  }
  @media (max-width: 560px) {
    .lmethod { padding: 56px 20px; }
    .lmethod__stats { gap: 20px 28px; }
  }

  /* Platform guarantees — big-number integrity bar on an INK near-black band.
     Placed between "What EntreDash does" and the advisory board. Deliberately Ink
     (#0F1714), NOT Heartwood — Heartwood already grounds the hero, the spark-to-
     business block, the marketplace and the Ask-AI band, so Ink differentiates
     this ledger and lets the Spark Bright figures glow. A whisper of Spark at the
     top edge adds energy without a slab. Numbers in Sentient (the registry tone),
     glowing Spark Bright; cream labels; faint cream hairline dividers. */
  .lguar {
    background: #0F1714;
    background-image: radial-gradient(80% 100% at 50% 0%, rgba(31,157,99,0.06), transparent 60%);
    padding: clamp(60px, 8vw, 88px) 32px;
  }
  .lguar__inner { max-width: 1080px; margin: 0 auto; }
  /* Eyebrow sized to match the page's other section eyebrows ("What EntreDash
     does" etc. — .section-label at 15px/700), so it reads as a peer section
     heading, not fine print. Stays on-dark brass (#C79A4E) for the Ink ground. */
  .lguar__eyebrow {
    display: flex; align-items: center; justify-content: center; gap: 10px;
    font-family: var(--f-mono); font-size: 15px; font-weight: 700; letter-spacing: 0.13em; text-transform: uppercase;
    color: #C79A4E; margin-bottom: 24px;
  }
  .lguar__lock { width: 15px; height: 15px; flex: none; color: #C79A4E; }
  .lguar__strip {
    display: grid; grid-template-columns: repeat(5, 1fr);
    border-top: 1px solid rgba(244,239,226,0.14); border-bottom: 1px solid rgba(244,239,226,0.14);
    padding: 38px 12px;
  }
  .lguar__cell { padding: 0 18px; text-align: center; position: relative; }
  .lguar__cell + .lguar__cell::before {
    content: ""; position: absolute; left: 0; top: 2px; bottom: 2px; width: 1px;
    background: rgba(244,239,226,0.14);
  }
  .lguar__num {
    font-family: var(--f-serif); font-weight: 400;
    font-size: clamp(2.875rem, 2.2rem + 1.2vw, 4rem); line-height: 1; letter-spacing: -0.01em;
    color: #4FD992; font-variant-numeric: tabular-nums;
  }
  .lguar__lab { font-size: 15px; line-height: 1.45; color: rgba(244,239,226,0.74); margin-top: 12px; }
  /* Grounded-in-records trust line below the strip. */
  .lguar__grounded { text-align: center; padding: 40px 28px 0; }
  .lguar__grounded-k {
    display: block; font-family: var(--f-mono); font-size: 12px; letter-spacing: 0.16em;
    text-transform: uppercase; color: rgba(244,239,226,0.6); margin-bottom: 12px;
  }
  .lguar__grounded-line {
    font-size: 16.5px; line-height: 1.55; color: rgba(244,239,226,0.78); max-width: 62ch; margin: 0 auto;
  }
  .lguar__grounded-line strong { color: #C79A4E; font-weight: 600; }
  .lguar__grounded-link {
    display: inline-block; margin-top: 16px; font-family: var(--f-mono); font-size: 12.5px;
    letter-spacing: 0.06em; text-transform: uppercase; color: #4FD992; cursor: pointer;
    background: none; border: none; text-decoration: underline; text-underline-offset: 3px;
  }
  .lguar__grounded-link:hover { color: #F4EFE2; text-decoration: underline; }
  @media (max-width: 720px) {
    .lguar { padding: clamp(48px, 12vw, 64px) 20px; }
    .lguar__strip { grid-template-columns: 1fr 1fr; gap: 26px 0; padding: 28px 6px; }
    .lguar__cell { padding: 0 14px; }
    /* Kill the vertical rule before the 3rd (start of row 2) and 5th (start of
       row 3) cells so 2-up rows don't show a stray left divider. */
    .lguar__cell:nth-child(3)::before,
    .lguar__cell:nth-child(5)::before { content: none; }
    /* The odd 5th cell centers across the full width on its own row. */
    .lguar__cell:nth-child(5) { grid-column: 1 / -1; }
    .lguar__num { font-size: 38px; }
    .lguar__grounded-line { font-size: 16px; }
  }

  /* "Ask an AI instead" band CSS now lives WITH the component
     (components/AskAiBand.tsx → ASK_AI_BAND_CSS), so it renders correctly
     wherever the band is used, including /ve. Intentionally not duplicated here. */

  /* Off-screen render skipping. These deep below-the-fold sections carry a lot
     of static DOM; content-visibility lets the browser skip their layout and
     paint until they scroll near the viewport, cutting the initial render cost
     of the long landing page (the "avoid an excessive DOM" / render-work item).
     Applied ONLY to static, non-animated sections that are NOT in-page anchor
     targets (so #pricing / #journey fragment scrolling stays pixel-accurate)
     and hold no JS-measured refs. contain-intrinsic-size uses the 'auto'
     keyword so the browser remembers each section's real height after its first
     render; the length is only the pre-first-render estimate. Browsers without
     content-visibility support (Safari < 18, Firefox < 125) ignore these two
     lines and render normally — it's a pure hint, nothing depends on it. */
  .lguar { content-visibility: auto; contain-intrinsic-size: auto 420px; }
  .lmethod { content-visibility: auto; contain-intrinsic-size: auto 900px; }
  .lfaq { content-visibility: auto; contain-intrinsic-size: auto 820px; }

  /* "Creating an account is free" clarifier — the reassurance beat that answers
     the "is signing up paid?" objection right after the how-it-works breakdown.
     A FAINT heartwood-tint full-bleed band (no card, no border), with an
     OUTLINED heartwood pill so it sits clearly below the hero's filled spark
     button and the nav's filled button in the CTA hierarchy — it clarifies
     without stealing the primary action. Not brass: brass reads as PAID in this
     brand, so a brass "free" CTA would send a mixed signal. */
  .lfree { background: rgba(27, 58, 46, 0.045); padding: clamp(44px, 6vw, 64px) 32px; }
  .lfree__inner {
    max-width: 560px; margin: 0 auto; text-align: center;
    display: flex; flex-direction: column; align-items: center;
  }
  .lfree__eyebrow {
    font-family: var(--f-sans); font-size: 12px; font-weight: 600;
    letter-spacing: 0.16em; text-transform: uppercase; color: var(--brass-deep);
  }
  .lfree__head {
    font-family: var(--f-serif); font-size: 29px; font-weight: 400;
    letter-spacing: -0.02em; color: var(--ink); margin-top: 14px; line-height: 1.15;
  }
  .lfree__sub {
    font-size: 15.5px; line-height: 1.6; color: rgba(15, 23, 20, 0.72);
    max-width: 46ch; margin: 14px auto 0;
  }
  .lfree__cta {
    display: inline-flex; align-items: center; gap: 8px; margin-top: 26px;
    padding: 12px 24px; border-radius: 999px; background: var(--heartwood);
    border: 1.5px solid var(--heartwood); color: var(--parchment);
    font-family: var(--f-sans); font-size: 15px; font-weight: 600; cursor: pointer;
    transition: background 0.14s ease, border-color 0.14s ease;
  }
  .lfree__cta:hover { background: var(--heartwood-dark); border-color: var(--heartwood-dark); }
  .lfree__cta svg { width: 16px; height: 16px; flex: none; }
  .lfree__micro {
    display: inline-flex; align-items: center; gap: 7px; margin-top: 16px;
    font-family: var(--f-mono); font-size: 11.5px; letter-spacing: 0.02em;
    color: rgba(15, 23, 20, 0.55);
  }
  .lfree__micro svg { width: 13px; height: 13px; flex: none; color: #1F9D63; }
  @media (max-width: 720px) { .lfree__head { font-size: 25px; } }
  @media (min-width: 1025px) {
    /* FIT THE FOLD (desktop + iPad landscape, e.g. iPad Air 13" at 1366x1024).
       The headline used to scale with the whole viewport height (9.9vh), which
       ignored the 101px nav and the padding, so the hero ran 13px past the fold
       at 1366x1024, 84px at 1440x900 and 119px at 1280x800 (measured). It now
       scales with the space actually left under the nav (--hero-avail), with
       tighter vertical padding and gaps, so the eyebrow-to-pricing group fits
       1280x800 through 1920x1080 and Safari's shorter landscape viewport. */
    .lhero {
      --hero-avail: calc(100svh - 101px - var(--promo-h, 0px));
      min-height: var(--hero-avail);
      padding-top: clamp(16px, 2.6vh, 56px);
      padding-bottom: clamp(16px, 2.6vh, 56px);
    }
    .lhero__headline {
      font-size: clamp(52px, min(9.7vw, calc(var(--hero-avail) * 0.098)), 132px);
      margin-bottom: clamp(10px, 1.4vh, 22px);
    }
    .lhero__sub {
      font-size: clamp(17px, min(1.5vw, calc(var(--hero-avail) * 0.024)), 22px);
      max-width: 58ch;
      margin-bottom: clamp(10px, 1.4vh, 20px);
    }
    .lhero__listed--center { margin-bottom: clamp(4px, 1vh, 16px); }
    .lhero__form { margin-bottom: clamp(8px, 1.2vh, 20px); }
  }
`;

/**
 * Marketing navigation for the public homepage. Nav and hero are one
 * masthead: at the top of the page the bar is fully transparent (it shows
 * the hero's Parchment with no border/fill), and once scrolled past ~48px
 * it solidifies into a Parchment bar with a hairline + faint shadow.
 *
 * Wordmark (dark ink, brass glyph) left · wayfinding text links center ·
 * Sign in + one Heartwood "Try free" button right. The three section
 * links smooth-scroll; Blog is a real /blog destination. An
 * IntersectionObserver drives the brass active-section underline. On
 * mobile it collapses to wordmark + hamburger -> a Parchment drawer.
 *
 * Calm and editorial by design: brass appears only in the wordmark glyph
 * and the single active underline; the one filled control is Heartwood.
 */

/* ---- Credibility rotator -------------------------------------------------
   A calm single-slot crossfade (NOT a marquee) at the bottom of the hero.
   One slide visible at a time, holding ~5.5s, dissolving over 700ms. The
   value-prop trio + statements are recessive mono; tester quotes are
   Sentient-italic pull-quotes at full Ink, per the brand's "an editorial
   pull-quote carries credibility better" rule. prefers-reduced-motion stops
   rotation entirely and shows the trio (slide 0) statically. */
type CredSlide =
  | { type: 'props'; items: { label: string; strong?: boolean }[] }
  | { type: 'statement'; text: string }
  | { type: 'quote'; quote: string; cite: string };

/* REAL tester quotes from the early-tester cohort, lightly condensed for
   length with meaning preserved (no invented testimonials, ever). Attribution
   is "Early tester" because we don't have names/roles on file; add first
   name + role here as those become available, e.g. cite: 'Marcus, machinist'.
   Keep each quote to ~70 chars so the strip stays one line on desktop. */
const CRED_TESTIMONIALS: CredSlide[] = [
  // — The pain we solve —
  {
    type: 'quote',
    quote: "I'm always shopping new ideas in my head, and I wanted an expert opinion.",
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I get a lot of ideas and wish I had someone to help me refine and build on them.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: "I didn't have the money for lawyers, so I never really looked into my ideas.",
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: "I've had good ideas that would make good inventions. This would be a helpful site.",
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: "I'd use it to figure out if my idea is original, or has any merit.",
    cite: 'Early tester',
  },
  // — What it gives you —
  {
    type: 'quote',
    quote: 'This basically takes the place of an entire sounding board advisory group.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'It gets you thinking without any commitment, and without anyone seeing your idea.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'You can also see if anyone else has already thought of it.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'It has great built-in features that help make your invention even better.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I read the sample memo, and something like that would be useful to me.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Lots of fun, with good ideas on how to proceed.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: "I'd use it to refine the business strategy, and recommend it to anyone.",
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I would use it to help continue to develop my patent.',
    cite: 'Early tester',
  },
  // — Why it matters / value —
  {
    type: 'quote',
    quote: 'In this economy, a creative and exciting way to realize the American dream.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: "I'd give it a try, especially since there's a free tier before you pay.",
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: '$9.99 seems reasonable to test the service and spark interest in more.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Free to try. Why not. It provides an edge above the rest.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'It safeguards your private business ideas, and you can try before you buy.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'It encourages creativity, critical thinking, and entrepreneurial skills.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Something you could own forever, and pass down to the next generation.',
    cite: 'Early tester',
  },
  // — More than a chatbot —
  {
    type: 'quote',
    quote: 'More helpful than just regular AI.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Different from most educational tools available for students.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I never thought I could get novel ideas each month. I may subscribe.',
    cite: 'Early tester',
  },
  // — Skeptic to convert —
  {
    type: 'quote',
    quote: "I went in sure I'd never use this. Now I want to give it a try.",
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I was actually very impressed, and decided to make an account.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'This product is excellent and worth considering. I will recommend it to others.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I really like all the features, and I would recommend it to a friend.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'In the unlikely event I needed it, I would sign up and explore it more.',
    cite: 'Early tester',
  },
  // — Look and feel —
  {
    type: 'quote',
    quote: 'The website looks very professional and interesting. I am impressed.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'A nice, friendly, trustworthy feel, and it is well written.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'This has an excellent look and feel. Professional. Something I would use for sure.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I like the idea. It looks high quality.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'A great idea, and the website itself looks very usable and very clean.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Engaging and easy to navigate. Looks like a good website.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Professional and interesting. It seems easy enough for anyone to use.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I like the privacy features and the messaging across the entire app.',
    cite: 'Early tester',
  },
  // — The pain it solves —
  {
    type: 'quote',
    quote: 'I wish I could brainstorm an idea the moment it hits me in the middle of the night.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'A great resource if I had an idea. I would definitely use it.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Get some feedback on an idea, see if it is realistic, then move forward if it is.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'It pointed out problems an examiner might find, so you can deal with them before filing.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'Useful for smaller teams who cannot keep a patent attorney on staff.',
    cite: 'Early tester',
  },
  // — Education + getting prepared —
  {
    type: 'quote',
    quote: 'The education center lets you get as informed as you want before you patent your idea.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'It walks you through the legal steps to secure your idea.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'It helps test your ability and make your invention even better.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I would use AI to find the right path to protect my creative capital.',
    cite: 'Early tester',
  },
  // — Worth it / would recommend —
  {
    type: 'quote',
    quote: 'It looks timely and essential, and I would support my kids using it.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'A good value for the service provided. I would pay for it.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I like it especially because of the free tier before deciding to upgrade.',
    cite: 'Early tester',
  },
  {
    type: 'quote',
    quote: 'I would share this with others if all goes well.',
    cite: 'Early tester',
  },
];

const CRED_STANDING: CredSlide[] = [
  {
    // Factual product claims only — no tester COUNT. The "200+ early testers"
    // number was dropped (a small figure can undercut credibility); the human
    // social proof is carried by the 50 real rotating tester quotes instead.
    type: 'props',
    items: [
      { label: 'Real prior art triage' },
      { label: 'Attorney-ready strategy memos' },
    ],
  },
  {
    type: 'statement',
    text: 'Built for independent inventors, STEM students, researchers, founders, and forward-thinking professionals.',
  },
  {
    type: 'statement',
    text: 'Strategy memos from your personal advisory board: a product strategist, an intellectual property (IP) consultant, and a financial analyst.',
  },
];

/* The value-prop trio leads (it's also the reduced-motion static fallback at
   index 0). Then testimonials flow in groups of 5, with a standing slide
   dropped in after each group — cycling the trio and the advisory-board
   statement — so the credibility messaging resurfaces every five quotes. */
const CRED_GROUP_SIZE = 5;
function buildCredSlides(): CredSlide[] {
  if (CRED_TESTIMONIALS.length === 0) return CRED_STANDING;
  const out: CredSlide[] = [];
  let standingIdx = 0;
  // Lead with the first standing slide (the trio).
  out.push(CRED_STANDING[standingIdx % CRED_STANDING.length]);
  standingIdx++;
  CRED_TESTIMONIALS.forEach((q, i) => {
    out.push(q);
    const isGroupEnd = (i + 1) % CRED_GROUP_SIZE === 0;
    const isLast = i === CRED_TESTIMONIALS.length - 1;
    // After every 5 quotes (but not trailing the very last one), rotate in
    // the next standing slide, alternating trio -> advisory -> trio ...
    if (isGroupEnd && !isLast) {
      out.push(CRED_STANDING[standingIdx % CRED_STANDING.length]);
      standingIdx++;
    }
  });
  return out;
}

const CRED_SLIDES = buildCredSlides();

function CredibilityRotator() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduce || paused || CRED_SLIDES.length <= 1) return;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % CRED_SLIDES.length),
      2600,
    );
    return () => clearInterval(id);
  }, [reduce, paused]);

  // Reduced-motion: pin to slide 0 (the value-prop trio) so the fallback is
  // exactly the old static credibility row.
  const activeIndex = reduce ? 0 : index;

  return (
    <div
      className="lhero__strip"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="cred-rotator" aria-live="off">
        {CRED_SLIDES.map((slide, i) => (
          <div key={i} className="cred-slide" data-active={i === activeIndex}>
            {slide.type === 'props' && (
              <span className="cred-props">
                {slide.items.map((it, n) => (
                  <span key={n}>
                    {n > 0 && <span className="lhero__dot">{'\u2002·\u2002'}</span>}
                    {it.strong ? <strong>{it.label}</strong> : it.label}
                  </span>
                ))}
              </span>
            )}
            {slide.type === 'statement' && (
              <span className="cred-statement">{slide.text}</span>
            )}
            {slide.type === 'quote' && (
              <span>
                <span className="cred-quote">
                  {'\u201C'}
                  {slide.quote}
                  {'\u201D'}
                </span>
                <span className="cred-cite">
                  {'·\u2002'}
                  {slide.cite}
                </span>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Collapsible FAQ item — click the question to expand/collapse the answer.
   Uses the grid-template-rows 0fr->1fr trick for a smooth auto-height
   animation, and a "+" that rotates to "x" as the open affordance. */
function FaqItem({
  q,
  a,
  isFirst,
  defaultOpen = false,
}: {
  q: string;
  a: string;
  isFirst: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        borderTop: isFirst ? '1px solid var(--border-hairline)' : 'none',
        borderBottom: '1px solid var(--border-hairline)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          padding: '22px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--ink)',
        }}
      >
        <h3
          style={{
            fontFamily: 'var(--f-serif)',
            fontWeight: 500,
            fontSize: 19,
            margin: 0,
            lineHeight: 1.35,
          }}
        >
          {q}
        </h3>
        <span
          aria-hidden="true"
          style={{
            flexShrink: 0,
            width: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            fontWeight: 300,
            lineHeight: 1,
            color: 'var(--brass-deep)',
            transition: 'transform 240ms ease',
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          +
        </span>
      </button>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          transition: 'grid-template-rows 260ms ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <p
            style={{
              color: 'var(--text-primary)',
              fontSize: 15,
              lineHeight: 1.6,
              opacity: 0.85,
              margin: 0,
              paddingBottom: 24,
            }}
          >
            {renderWithBrand(a)}
          </p>
        </div>
      </div>
    </div>
  );
}

// Plan comparison matrix data. The subscription ladder is CUMULATIVE — every
// row Starter includes, Standard and Pro also include; every row Standard adds,
// Pro also includes. So each higher tier is a strict superset of the one below
// it ("everything in the lower tier, plus more"). Pay-per-invention is a
// separate purchase, grouped on its own so its blank monthly cells never read
// as the product being weak. A cell is `true` (included), `false` (not part of
// this plan), or a string (a count / "Unlimited").
const COMPARE_GROUPS: Array<{
  title: string;
  caption?: string;
  rows: Array<{ label: string; cells: Array<boolean | string> }>;
}> = [
  // Cell order matches the ladder above: [Starter, Standard, Plus, Pro].
  {
    title: 'Every month',
    rows: [
      { label: 'Unlimited idea capture', cells: [true, true, true, true] },
      // Spark sits right after capture and before sessions — it is the
      // ideation front door that turns a raw frustration into a shaped idea
      // worth taking into a session. Per-tier monthly allowance.
      { label: 'Spark ideation runs per month', cells: ['2', '5', '10', '20'] },
      { label: 'Strategy sessions per month', cells: ['1', '3', '12', '30'] },
      { label: 'Strategy memos per month', cells: ['1', '3', '12', '30'] },
      { label: 'Choose any specialist each session', cells: [true, true, true, true] },
      { label: 'Real prior-art citations in every memo', cells: [true, true, true, true] },
      { label: 'Read your full memo in the app', cells: [true, true, true, true] },
      { label: 'Confidential PDF download', cells: [false, true, true, true] },
      { label: 'Full Education Center', cells: [false, true, true, true] },
      { label: 'Vocabulary & spaced-repetition study cards', cells: [false, true, true, true] },
      { label: 'Streaks, levels, and badges', cells: [false, true, true, true] },
    ],
  },
  {
    // The build tools get their own group, sitting immediately after capacity
    // rather than buried among general features — this is the capability
    // boundary that Plus exists to sell, so the table should show it as a
    // block rather than three scattered rows.
    title: 'The build tools · Plus and above',
    caption: 'For turning a validated idea into something you can actually make.',
    rows: [
      { label: 'AI design breakdown, with material options', cells: [false, false, true, true] },
      { label: 'Customer discovery interview kit', cells: [false, false, true, true] },
      { label: 'Business Model Canvas generation', cells: [false, false, true, true] },
      { label: 'Design Workspace', cells: [true, true, true, true] },
      { label: 'Ideas per month with the build tools', cells: [false, false, '4', '10'] },
    ],
  },
  {
    title: 'Scale',
    rows: [
      { label: 'Priority memo compilation', cells: [false, false, false, true] },
      { label: 'Early access to new modules', cells: [false, false, false, true] },
    ],
  },
];

// One comparison cell: a heartwood check (included), a calm mist rule (not
// included — never a punitive X), or a plain value (count / "Unlimited").
function renderCompareCell(v: boolean | string) {
  if (v === true) return <span className="cmp__yes" aria-label="Included">✓</span>;
  if (v === false) return <span className="cmp__no" aria-label="Not included" />;
  return <span className="cmp__val">{v}</span>;
}

export function LandingPage({ light = false }: { light?: boolean } = {}) {
  const [, navigate] = useLocation();
  const openAuthModal = useAuthModal((s) => s.openModal);
  const authedUser = useAuth((s) => s.user);

  // Start subscribing to a specific paid plan. Logged-in users go straight to
  // the subscription page (their plan preselected); anonymous visitors get the
  // signup popup, with the chosen plan carried through so they land on the
  // subscription page once their account exists. The plan id is the backend
  // tier id ('inventor' = Standard, 'startup' = Pro).
  const startSubscribe = (plan: PlanId) => {
    // Carry the chosen plan AND the annual/monthly toggle choice through to the
    // subscription page (and through signup for anonymous visitors), so live
    // checkout charges exactly what was picked here.
    const target = `/settings/subscription?plan=${plan}&billing=${billing}`;
    if (authedUser) {
      navigate(target);
    } else {
      openAuthModal('signup', target);
    }
  };
  const [ideaText, setIdeaText] = useState('');
  // The Spark tool modal, launched from the hero's secondary "Try Spark" entry.
  const [sparkOpen, setSparkOpen] = useState(false);
  const heroInputRef = useRef<HTMLTextAreaElement | null>(null);
  const closeLedeRef = useRef<HTMLHeadingElement | null>(null);

  // Below-the-fold code-split gate. `belowReady` flips true once the deferred
  // section chunks (TheClimb, PricingStack, …) have preloaded; until then their
  // slots render nothing, so the hero's entry chunk boots and animates without
  // competing with them. Because it's all below the fold, filling those slots in
  // later causes no visible layout shift. See the lazy(...) block up top.
  const [belowReady, setBelowReady] = useState(false);
  // Holds the prerender snapshot open until the deferred chunks have loaded, so
  // the crawler still captures the full page. Opened in a LAYOUT effect (which
  // flushes before the beacon's passive effect reads the gate) and always
  // released — on load OR unmount — so it can never strand a later route.
  const gateReleaseRef = useRef<null | (() => void)>(null);
  useLayoutEffect(() => {
    gateReleaseRef.current = openPrerenderGate();
    return () => {
      gateReleaseRef.current?.();
      gateReleaseRef.current = null;
    };
  }, []);

  // Preload the below-the-fold chunks during idle time, then render them and
  // release the prerender gate. requestIdleCallback yields the hero's paint and
  // first conveyor frames the main thread first; the timeout (and the setTimeout
  // fallback for browsers without rIC — Safari) guarantees it still fires
  // promptly, which also keeps the headless crawler's gate from waiting long.
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      preloadBelowFold()
        // A genuinely failed (or reload-guard-blocked) preload must not surface
        // as an uncaught rejection — the lazy <Suspense> sections below still
        // recover on render via loadChunkWithRetry. Swallow it, then release the
        // gate either way so the page never stays behind the preload.
        .catch(() => {})
        .finally(() => {
          if (cancelled) return;
          setBelowReady(true);
          gateReleaseRef.current?.();
        });
    };
    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      idleId = w.requestIdleCallback(run, { timeout: 1500 });
    } else {
      timerId = setTimeout(run, 200);
    }
    return () => {
      cancelled = true;
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timerId !== undefined) clearTimeout(timerId);
    };
  }, []);

  // Instant-hero handoff: index.html paints a static above-the-fold hero on
  // the home route (a mobile variant ≤900px and a desktop variant ≥1025px) so
  // the headline (the LCP element) appears without waiting for this bundle to
  // boot. Now that this page has mounted, the real hero is in the DOM —
  // cross-fade the active overlay out over it. rAF ensures the real hero has
  // painted before the fade begins, so there's no dip. No-op on non-home routes
  // and in the 901–1024 band (no overlay is shown there). See index.html.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      (window as unknown as { __dismissInstantHero?: () => void }).__dismissInstantHero?.();
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Closing-CTA heading types in line-by-line (a soft ink-wipe per line) and
  // LOOPS: it types out, holds, fades away, and re-types, for as long as it is
  // in view. Armed via JS so a no-JS render shows the full heading; reduced
  // motion skips straight to a static, fully-visible state (no loop).
  useEffect(() => {
    const el = closeLedeRef.current;
    if (!el) return;
    el.classList.add('is-armed');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('is-revealing');
      return;
    }

    // One full cycle: type out (~4.6s of staggered wipes) → hold (~3s) → fade
    // the whole heading away (~0.5s) → brief blank (~0.5s) → repeat. Timings are
    // slower than the one-shot version so the type-out reads deliberately.
    const TYPE_OUT_MS = 4600; // last line + caret settled
    const HOLD_MS = 3000;
    const FADE_MS = 500;
    const BLANK_MS = 500;
    const timers: number[] = [];
    let running = false;

    const clearTimers = () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers.length = 0;
    };

    const cycle = () => {
      // Reset to the hidden/armed state, force a reflow, then replay the wipes.
      el.classList.remove('is-clearing', 'is-revealing');
      void el.offsetWidth;
      el.classList.add('is-revealing');
      // After the type-out and hold, fade the whole heading out...
      timers.push(
        window.setTimeout(() => {
          el.classList.add('is-clearing');
          // ...then, once faded and blanked, run the next cycle.
          timers.push(window.setTimeout(cycle, FADE_MS + BLANK_MS));
        }, TYPE_OUT_MS + HOLD_MS),
      );
    };

    const start = () => {
      if (running) return;
      running = true;
      cycle();
    };
    const stop = () => {
      running = false;
      clearTimers();
    };

    // Only animate while the heading is on screen (start on enter, pause on
    // exit) so the loop never burns cycles off-screen.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) start();
        else stop();
      },
      { threshold: 0.35, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimers();
    };
  }, []);

  // Confidentiality gate for anonymous visitors. Shown the first time
  // a visitor on this browser submits the hero form — and only that
  // first time. Subsequent /try attempts skip the modal because the
  // acceptance timestamp is stored in localStorage. The same flag is
  // also respected by the post-signup IdeaForm/Session gates, so a
  // tester who accepted as a visitor doesn't see the modal again
  // after signing up.
  const [showConfidentiality, setShowConfidentiality] = useState(false);

  // Pricing billing period. Defaults to 'annual' — the lowest displayed price
  // (Standard annual) and consistent with the commercial panel's anchor. Only the
  // Standard and Pro columns respond; Starter ($0) and Pay-per-invention are
  // unaffected.
  const [billing, setBilling] = useState<'annual' | 'monthly'>('annual');

  // Collapsible "Compare all features" matrix below the four pricing columns.
  const [showCompare, setShowCompare] = useState(false);

  // Auto-expand the comparison matrix when the visitor arrives via the nav's
  // "Compare plans" link (#plan-comparison) — otherwise they'd land on a
  // collapsed toggle and see nothing to compare. Handles both a fresh page
  // load with the hash and an in-page hash change (already on the landing page).
  useEffect(() => {
    const openIfComparing = () => {
      if (window.location.hash === '#plan-comparison') setShowCompare(true);
    };
    openIfComparing();
    window.addEventListener('hashchange', openIfComparing);
    return () => window.removeEventListener('hashchange', openIfComparing);
  }, []);

  // Tab-scoped: mark this as the most recent landing page so demo pages
  // (sample memo, education center) route back to '/' on exit — even if
  // the visitor has a stale audience='student' flag from a previous visit
  // to /students.
  useEffect(() => {
    markLandingVisit('/');
    // Stamp the A/B landing variant (write-once, first landing wins) so the
    // heartbeat + signup attribute this visitor's eventual conversion to the
    // page that acquired them. control = the dark homepage, v2 = the light one.
    stampLandingVariant(light ? 'v2' : 'control');
  }, [light]);

  /**
   * Commit the anonymous state and navigate to /try. Pulled out of
   * startSession so the modal can re-fire it on accept without having
   * to reach back through the form event chain.
   */
  const commitStart = (text: string) => {
    writeAnonState({
      ideaText: text,
      messages: [],
      extractedFacts: {},
      sessionComplete: false,
    });
    navigate('/try');
  };

  // Free-first action: bring the visitor to the hero idea box (the truly
  // free, no-signup path) and focus it. Reused by the closing CTA and the
  // Free pricing column so "Start free" always lands on the idea cursor.
  const scrollToHeroIdea = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Focus after the scroll settles (~600ms) so the textarea is visible
    // when the cursor lands in it.
    setTimeout(() => heroInputRef.current?.focus(), 650);
  };

  const startSession = (e?: FormEvent) => {
    e?.preventDefault();
    const text = ideaText.trim();
    if (!text) {
      heroInputRef.current?.focus();
      return;
    }
    // Gate: pop the Confidentiality Terms modal on every submission
    // during the alpha. Easier for the team to verify the modal still
    // works on every test, and the friction is small (one accept per
    // idea is still negligible). Once we exit alpha we can revert to
    // the "once per browser" check below:
    //   if (readAnonymousConfidentialityAck() === 0) { ... }
    // — the helper is still imported and the localStorage flag is
    // still written on accept, so the downstream IdeaForm and Session
    // gates inside the app continue to recognise prior acceptance.
    setShowConfidentiality(true);
  };

  return (
    <div
      className={`landing-page-body${light ? ' landing--light' : ''}`}
      style={{ background: 'var(--surface-page)', minHeight: '100dvh' }}
    >
      {/* Temporary launch-promo bar. ABOVE the sticky nav in the DOM on purpose,
          so it scrolls away and the nav keeps the top edge — a pinned promo bar
          would cost viewport on every screen of a long landing page. It hides
          itself once the Stripe code expires; see promo.ts. */}
      <AnnouncementBar />
      {/* Marketing nav + light editorial hero — one masthead. The nav's
          primary action is "Sign up" (→ account creation), distinct from the
          hero's anonymous "Test my idea" so the two CTAs don't duplicate. */}
      {/* Light variant forces the nav's SOLID treatment (dark wordmark +
          ink links, readable over the light ground); the control keeps the
          transparent-over-dark-hero nav. */}
      <MarketingNav variant={light ? 'solid' : 'transparent'} />
      {/* Floating "← Home" pill on the home page too (founder request to have it
          everywhere). Placed right after the nav so its sentinel sits just below
          the nav — on this long landing it acts as a scroll-to-top / home anchor
          once the nav scrolls away. */}
      <HomePill />

      {/* Primary content landmark. Wraps every content section (hero → sign-off
          band) so the page exposes exactly one <main> for assistive tech and
          Lighthouse's main-landmark check. Nav/promo chrome and the footer sit
          outside it. The landing section selectors are scoped through this
          layer (`.landing-page-body > main > section`). */}
      <main>
      <section id="hero" className="lhero" data-surface={light ? undefined : 'dark'}>
        {/* The full rolling "Featured on" marquee was moved to the closing
            sign-off band (under the "Spark to business" tagline). The hero now
            leads with a curated two-logo "Listed on" row (Capterra + Product
            Hunt) inside the left column, above the headline — matching /ve. */}
        {/* Editorial eyebrow — a single centered line spanning the FULL hero
            width at the top (a top banner), so it fills the empty space above
            the right-side card carousel rather than sitting only over the left
            column. Brass ticks flank it on both sides. */}
        <div className="lhero__eyebrow lhero__eyebrow--top">
          The 0-to-1 workspace for anyone who&apos;s thought, &ldquo;why doesn&apos;t this exist?&rdquo;
        </div>
        {/* Curated "Listed on" trust row (Capterra + Product Hunt), centered
            full-width directly under the eyebrow banner — proof is read up front,
            on the same center axis as the eyebrow, above the split content. Logos
            sit in light pills (matching the marquee badge treatment) so both read
            cleanly on the dark ground; each links to its live listing. */}
        <div className="lhero__listed lhero__listed--center">
          <span className="lhero__listed-label">Listed on</span>
          <div className="lhero__listed-logos">
            <a
              className="lhero__listed-link"
              href="https://www.capterra.com/p/10055800/EntreDash/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="EntreDash on Capterra"
            >
              <span className="lhero__listed-cap" aria-hidden="true" />
            </a>
            {/* The two #1 awards (Fazier Product of the Day, PeerPush Product of the
                Week), paired in the middle of the row: the strongest proof anchors
                the centre line. Official badges hotlinked; sizes reserved so the
                row can't reflow while they load. */}
            <span className="lhero__listed-awards">
              <a
                className="lhero__listed-award"
                href="https://fazier.com/launches/entredash"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="EntreDash ranked #1 Product of the Day on Fazier"
              >
                <img
                  src="https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=12121&badge_type=daily&variant=2&theme=dark"
                  alt="EntreDash on Fazier: #1 Product of the Day"
                  width={155}
                  height={50}
                />
              </a>
              <a
                className="lhero__listed-award lhero__listed-award--pp"
                href="https://peerpush.com/p/entredash"
                target="_blank"
                rel="noopener"
                aria-label="EntreDash ranked #1 Product of the Week on PeerPush"
              >
                {/* Our rendering of PeerPush's award for the dark hero: their medal in
                    brand brass beside a white title and "1st", at Fazier's height
                    (their official badge is an opaque white card). Links to the listing. */}
                <img
                  src="/badges/peerpush-product-of-the-week-dark.png"
                  alt="EntreDash on PeerPush: 1st Product of the Week"
                  width={168}
                  height={50}
                />
              </a>
            </span>
            <a
              className="lhero__listed-link"
              href="https://www.producthunt.com/products/entredash-spark-to-business"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="EntreDash on Product Hunt"
            >
              <span className="lhero__listed-ph">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M13.604 8.4h-3.405V12h3.405c.995 0 1.801-.806 1.801-1.801 0-.993-.805-1.799-1.801-1.799zM12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm1.604 14.4h-3.405V18H7.801V6h5.804c2.319 0 4.2 1.88 4.2 4.199 0 2.321-1.881 4.201-4.201 4.201z" />
                </svg>
                Product Hunt
              </span>
            </a>
          </div>
        </div>
        <div className="lhero__split">
          {/* Left: light Parchment content side — leads the page, keeps the
              warm/inviting register, and holds the idea-input form as the
              unmistakable primary action. */}
          <div className="lhero__content">
            <div className="lhero__main">
              {/* "Spark to business" positioning line as a 3-line signpost.
                  Leads with "Your" (personal ownership up front) and ends on the
                  outcome "a real business" — "real" is the credibility note that
                  echoes the brand's "real" motif (real market data, real prior
                  art). Two-tone payoff: "Your spark to a" in parchment, the green
                  payoff "real business." in on-dark spark green (the
                  forward/action accent). Three lines: line 1 "Your spark", line 2
                  "to a real" ("to a" parchment + "real" green), line 3
                  "business." (green + brass underline). Supersedes "Random spark
                  to your business." (dropped "random"; front-loaded "Your"). */}
              <h1 className="lhero__headline">
                <span className="lhero__headline-line">Your spark</span>
                <br />
                <span className="lhero__headline-line lhero__headline-line--mid">
                  to a <span className="lhero__headline-accent-word">real</span>
                </span>
                <br />
                <span className="lhero__headline-line lhero__headline-accent">business.</span>
              </h1>
              {/* Subhead does the PLAIN-ENGLISH explaining (testers said the
                  hero read as mood, not mechanism). Rewritten after testers said
                  the old "strategy session / commercial potential" phrasing was
                  unclear: now names the concrete mechanic (answer a few
                  questions), the REAL sources it actually retrieves (Census, SEC,
                  competitor data — not an inflated "50+ sources" claim), and lands
                  on the exact question a first-time inventor is anxious about as
                  the brass-accent payoff. Describes the FREE front-door session
                  (Needs Discovery / Product Strategist), which answers "is there a
                  real market?", NOT the later go/pivot/stop patent verdict. */}
              <p className="lhero__sub">
                In under 15 minutes, your AI advisory board (a product strategist, an IP
                consultant, and a financial analyst) pressure-tests your idea and gives
                you a straight answer:{' '}
                <span className="lhero__sub-accent">is this worth building?</span>
              </p>
              {/* Mobile-only condensed subhead — same concise sentence as the
                  desktop subhead now (it's short enough to share), so first-time
                  visitors on phones get the same plain explanation. */}
              <p className="lhero__sub-mobile">
                In under 15 minutes, your AI advisory board (product, IP, and finance)
                pressure-tests your idea and gives you a straight answer:{' '}
                <span className="lhero__sub-accent">is this worth building?</span>
              </p>
              {/* Idea composer, ported from /ve: a cream entry-box CARD holding
                  the textarea, with the confidentiality reassurance sitting right
                  at the cursor (inside the card) and the "Get my verdict" button
                  docked bottom-right. Below the card, an outlined Spark pill as
                  the clearly-subordinate second door, with the free/no-card line
                  beside it. Two clear CTAs, matching /ve. Card values follow the
                  LIGHT-ground rules (it's a cream card); the pill + free line
                  follow on-dark rules (they sit on the heartwood ground). */}
              <form onSubmit={startSession} className="lhero__form">
                <div className="lhero__box">
                  <TextareaAutosize
                    ref={heroInputRef}
                    value={ideaText}
                    onChange={(e) => setIdeaText(e.target.value)}
                    minRows={2}
                    maxRows={5}
                    placeholder="Got an idea, or just a problem? Plain words are fine."
                    className="lhero__boxinput"
                    aria-label="Describe your idea"
                    aria-describedby="hero-secure"
                    /* '/' is a recorded marketing route; mask so the typed idea
                       is never captured by session recording. */
                    data-clarity-mask="true"
                  />
                  <div className="lhero__box-foot">
                    <span className="lhero__box-privacy" id="hero-secure">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="5" y="11" width="14" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                      Private and encrypted. Never shared, never used to train AI.
                    </span>
                    {/* Docked primary — spark #1F9D63 (NOT the on-dark spark-light):
                        its local ground is the cream card, where spark-light washes
                        out; spark green + ink reads crisp. */}
                    <Button type="submit" variant="spark" className="lhero__box-go">
                      Get my free verdict &rarr;
                    </Button>
                  </div>
                </div>
                {/* Proof door, docked directly under the box: a live guest
                    session on a sample idea, for the skeptic not ready to type
                    their own. A quiet brass-underline line, never a button — it
                    stays subordinate to the "Get my verdict" primary. */}
                <div className="lhero__demoline">
                  Skeptical?{' '}
                  <Link href="/demo/live">Watch a real strategy session on a sample idea &rarr;</Link>
                </div>
                {/* One row directly under the box: the outlined brass Spark pill
                    on the LEFT as the clearly-subordinate second door, and the
                    "Free first try" reassurance on the RIGHT, right-docked under
                    the "Get my verdict" button so "free" still reads against the
                    primary action. type=button so the pill never submits. */}
                <div className="lhero__subrow">
                  <button type="button" className="lhero__sparkpill" onClick={() => setSparkOpen(true)}>
                    {/* Lightning bolt — an electrical "spark" (not a 4-point
                        sparkle, which reads as the Gemini mark). */}
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 Z" />
                    </svg>
                    <span className="lhero__sparkpill-q">No formed idea yet?</span>
                    <span className="lhero__sparkpill-a">Spark one free</span>
                  </button>
                  <span className="lhero__freeline">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    Free first try &middot; No card
                  </span>
                </div>
              </form>
              {/* Free-first PRICING cue + the sample-memo escape hatch on ONE
                  line (space-optimized): the price is the #pricing link, and
                  "See a sample memo" previews a real finished memo for visitors
                  hesitant to type into a blank box. Both sit UNDER the CTA so the
                  free on-ramp stays dominant.

                  Anchor on the entry price for a real paid deliverable — a
                  single strategy session ($9.99) — rather than the cheapest
                  subscription. Pay-as-you-go framing: buy only what you need, no
                  subscription. Sourced from SINGLE_SESSION_PRICE_CENTS so it never
                  drifts. No `/mo` — these are one-off purchases, not a recurring
                  plan. */}
              <div className="lhero__pricing">
                <span>
                  {/* `US$` prefix, not a `USD` suffix. A suffix arrives after the
                      reader has already parsed the number in their own currency
                      and anchored on it, so it reads as a correction. This is the
                      funnel's anchor price and frequently the only one a visitor
                      sees, so it must disambiguate on first read. */}
                  Pay as you go, from <a href="#pricing">US{formatPrice(SINGLE_SESSION_PRICE_CENTS)}</a>
                </span>
                <span className="lhero__pricing-sep" aria-hidden="true">&middot;</span>
                <Link href="/demo/sample-needs-discovery" className="lhero__pricing-sample">
                  See a sample memo &rarr;
                </Link>
              </div>

            </div>
            {/* The old bottom "Free. No sign-up needed to try." micro-line was
                removed — the FREE · NO CARD · NO SIGN-UP receipt now carries
                that promise directly under the CTA, where a skimmer reading
                only the button region still sees it. */}
          </div>

          {/* Right: the product itself, shown as a calm cascade of real
              deliverable cards on the heartwood ground (a Go verdict memo with
              the brass seal, a 5-yr business case, a Boolean prior-art search),
              under a slim EntreDash brand header. This replaces the old stock
              desk photo — it shows the premium output that is the product's
              biggest differentiator, the moment a skeptic is deciding whether
              we're real. See HeroShowcase. */}
          <aside className="lhero__panel">
            <HeroShowcase />
          </aside>
        </div>

        <HomeScrollCue />
      </section>

      <style>{LANDING_TOP_CSS}</style>

      {/* Product demo video — sits immediately after the first viewport. A dark
          shelf continues the hero, and a 16:9 click-to-play frame straddles the
          seam into the parchment below, bridging the cinematic hero into the
          lighter sections. Click-to-play facade: the video file loads only on
          click, so this never touches LCP or the Lighthouse score. */}
      <Deferred ready={belowReady}>
        <DemoVideo />
      </Deferred>

      {/* "Creating an account is free" clarifier band. Sits directly under the
          demo video and above "What EntreDash does": once a visitor has watched
          the product in action, the very next beat answers the "is signing up
          paid?" objection before the how-it-works breakdown. All the page's
          "free" messaging above this point is glued to TRYING (the anonymous
          verdict flow); this is the one beat that attaches "free" to the
          ACCOUNT. Deliberately below the fold — a filled heartwood pill, kept
          off the hero's Spark green and off brass (brass reads as paid here) —
          so it never competes with the hero's "Get my verdict" primary action. */}
      <section className="lfree">
        <div className="lfree__inner">
          <span className="lfree__eyebrow">Signing up</span>
          <div className="lfree__head">Creating an account is free.</div>
          <p className="lfree__sub">
            No credit card required, and your first strategy verdict is free, yours to keep.
            You only pay if you choose to run another analysis or subscribe.
          </p>
          <button
            type="button"
            className="lfree__cta"
            onClick={() => openAuthModal('signup')}
          >
            Sign up free
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
          <span className="lfree__micro">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Free · No card · Yours to keep
          </span>
        </div>
      </section>

      {/* How it works — the self-paced 3-step explainer of the FREE FIRST
          SESSION, sitting immediately below the hero so a visitor understands
          "what happens in the next five minutes if I type my idea in" BEFORE
          committing. Directly answers the #1 tester blocker ("I won't submit
          until I understand the process"). Distinct from TheClimb below, which
          shows the full 8-deliverable arc; this shows only Describe → Discuss
          → Decide, with real UI fragments. The confidentiality ledger now lives
          at the BOTTOM of this section (under the free/no-card foot line). */}
      <Deferred ready={belowReady}>
        <HowItWorksSteps />
      </Deferred>

      {/* Platform guarantees — the big-number integrity bar on an Ink near-black
          band. Sits after "What EntreDash does" (the dashboard-journey explainer)
          and before the AI advisory board — a dark ledger interlude between two
          Parchment sections, deliberately Ink (not Heartwood) so it doesn't echo
          the other dark-green bands, with the five figures glowing in Spark Bright.
          0/0/100% are the privacy + ownership promise, 200+ is the model ensemble,
          100+ is the grounded-records line below. */}
      <section className="lguar" data-surface="dark">
        <div className="lguar__inner">
          <span className="lguar__eyebrow">
            <svg className="lguar__lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            What our platform guarantees
          </span>
          <div className="lguar__strip">
            <div className="lguar__cell">
              <div className="lguar__num">0</div>
              <div className="lguar__lab">of your ideas used to train AI</div>
            </div>
            <div className="lguar__cell">
              <div className="lguar__num">0</div>
              <div className="lguar__lab">humans ever read your idea</div>
            </div>
            <div className="lguar__cell">
              <div className="lguar__num">200+</div>
              <div className="lguar__lab">frontier models on tap, not just one</div>
            </div>
            <div className="lguar__cell">
              <div className="lguar__num">100+</div>
              <div className="lguar__lab">patent offices covered worldwide</div>
            </div>
            <div className="lguar__cell">
              <div className="lguar__num">100%</div>
              <div className="lguar__lab">yours, always</div>
            </div>
          </div>
          {/* Grounded-in-records: one trust line here; the full categorized
              source list lives on the methodology page. */}
          <div className="lguar__grounded">
            <span className="lguar__grounded-k">Grounded in authoritative public records</span>
            <p className="lguar__grounded-line">
              Prior art from <strong>100+ national offices</strong>, federal market data, academic
              literature, and a live scan of the open web.
            </p>
            <button className="lguar__grounded-link" onClick={() => navigate('/methodology')}>
              See every source &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* Advisory board — the animated node network (shared with the /ve hero),
          grouped directly under "How it works": after seeing Describe → Discuss
          → Decide, the visitor sees the board of specialist agents that does
          that work. Same light-ground component, centered in its own band. */}
      <section className="lhow-board">
        <div className="lhow-board__inner">
          <Deferred ready={belowReady}>
            <AdvisoryBoardDiagram />
          </Deferred>
        </div>
      </section>

      {/* The engine — a technical "spec-sheet" describing the orchestration
          method that makes EntreDash distinct from a chatbot. Deliberately a
          DIFFERENT form from the sections around it: static, tabular, mono-dense,
          left/right asymmetric (an editorial masthead + a hairline readout
          ledger), so it doesn't echo the 3-step "How it works" row or the
          animated diagram above it. Every data token is real. */}
      <section className="lmethod">
        <div className="lmethod__inner">
          <div className="lmethod__mast">
            <span className="lmethod__eyebrow">The EntreDash Method</span>
            <h2 className="lmethod__h">
              Not a chatbot.
              <br />
              <em>An orchestration engine.</em>
            </h2>
            <p className="lmethod__deck">
              Ten-plus agents, three frontier models, one router. Each task goes to whichever model handles it best,
              not whichever one happens to be loaded.
            </p>
            <div className="lmethod__stats">
              <div className="lmethod__stat">
                <span className="lmethod__stat-n">3</span>
                <span className="lmethod__stat-l">Frontier models</span>
              </div>
              <div className="lmethod__stat">
                <span className="lmethod__stat-n">200+</span>
                <span className="lmethod__stat-l">Models on tap</span>
              </div>
              <div className="lmethod__stat">
                <span className="lmethod__stat-n">6</span>
                <span className="lmethod__stat-l">Live record systems</span>
              </div>
            </div>
          </div>

          <div className="lmethod__ledger">
            <div className="lmethod__row">
              <div className="lmethod__rhead">
                <span className="lmethod__idx">01</span>
                <span className="lmethod__kick">Model routing</span>
              </div>
              <p className="lmethod__lead">Every task goes to the model that&apos;s best at it.</p>
              <p className="lmethod__body">
                No single model runs the whole session. Claude, Gemini, and GPT all get called in, and each task goes to
                whichever one handles it best.
              </p>
              <p className="lmethod__data">
                <b>Claude</b> · Gemini · GPT · +200 on tap
              </p>
            </div>

            <div className="lmethod__row">
              <div className="lmethod__rhead">
                <span className="lmethod__idx">02</span>
                <span className="lmethod__kick">Live records</span>
              </div>
              <p className="lmethod__lead">Answers come from public records, not memory.</p>
              <p className="lmethod__body">
                These are real systems of record, not the model&apos;s training data. Every session queries them live, at
                the moment you ask, so the results are current, not recalled.
              </p>
              <p className="lmethod__data">
                <b>100+ patent offices covered</b> · SEC EDGAR · US Census · BLS · OpenAlex · live web
              </p>
            </div>

            <div className="lmethod__row">
              <div className="lmethod__rhead">
                <span className="lmethod__idx">03</span>
                <span className="lmethod__kick">Practitioner models</span>
              </div>
              <p className="lmethod__lead">Each agent thinks like a specific professional.</p>
              <p className="lmethod__body">
                One agent runs product-strategy frameworks. Another structures a session the way a senior US patent
                attorney would. A third runs the diligence of a VC associate.
              </p>
              <p className="lmethod__data">
                <b>Product Strategist</b> · Patent Attorney · VC Associate
              </p>
            </div>

            <div className="lmethod__row lmethod__row--out">
              <div className="lmethod__rhead">
                <span className="lmethod__idx">04</span>
                <span className="lmethod__kick">One verdict</span>
              </div>
              <p className="lmethod__lead">All of it lands in a single, cited verdict.</p>
              <p className="lmethod__body">
                Every claim in the verdict traces back to a source you can check. And the verdict is honest: it can say
                no.
              </p>
              <p className="lmethod__data">
                <b>1 verdict</b> · every claim sourced · can say &ldquo;no&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TheClimb — the merged "annotated climb" that LEADS the body. Replaces
          the former JourneyStaircase + DeliverablesGallery (which duplicated
          each other): the journey breadth and the real-deliverable proof now
          live in ONE section, where the 6 deliverable cards are the stations on
          a spark-green spine. Also the nav's "How it works" target (#journey):
          the whole idea-to-business climb is the best answer to "how does this
          work?". The DeliverablesGallery component still serves the /students
          page; on the landing page TheClimb supersedes it. */}
      <Deferred ready={belowReady}>
        <TheClimb />
      </Deferred>

      {/* Marketplace spotlight — replaces the old memo-proof section (the
          memos are already shown throughout the climb above). Answers the
          inventor's next question after a verdict: who do I take this to? */}
      <Deferred ready={belowReady}>
        <MarketplaceSpotlight />
      </Deferred>

      {/* SECTION 3B: Two pathways from here.

          Once the inventor has a verdict, they face a real decision:
          file the patent themselves (pro se) or hire an attorney.
          Both are legitimate; both have tradeoffs. This section makes
          the tradeoffs explicit and shows that EntreDash supports BOTH
          pathways, not just step 4 of the journey.

          The previous framing implied EntreDash "handed off" to an
          attorney after the memo. The honest framing is: EntreDash is
          a co-pilot through the full process, helping you do it
          yourself OR helping you work efficiently with a pro. This
          section establishes that positioning publicly. */}
      {/* PRICING — shared PricingStack in "landing" mode: pay-as-you-go tickets
          only, followed by a doorway CTA to the full /plans page (credit packs,
          subscriptions, patent prep, compare-plans matrix all live there). The
          homepage stays light and commits to the simplest entry point. Embedded
          brings its own Lab scope + styles + Stripe line. Keeps id="pricing" so
          the hero's #pricing link still lands here. */}
      <Deferred ready={belowReady}>
        <PricingStack variant="landing" embedded />
      </Deferred>


      {/* SECTION 7: FAQ */}
      {/* "Ask an AI instead" trust band — sits between the light Method/Guarantees
          run and the light FAQ, so the dark heartwood reads as a deliberate
          punctuation (and doesn't stack against the dark Final CTA below the FAQ). */}
      <Deferred ready={belowReady}>
        <AskAiBand />
      </Deferred>

      <section className="lfaq" style={{ padding: '128px 32px', background: 'var(--surface-card)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div className="section-label" style={{ marginBottom: 24 }}>FAQ</div>
          <h2 className="f-display" style={{ fontSize: 'clamp(28px, 4.5vw, 44px)', marginBottom: 56, fontWeight: 400 }}>
            Honest answers to fair questions.
          </h2>
          {/* Shared objection-handling FAQ copy (same source as /ve), rendered
              in the main page's own FaqItem styling. */}
          {FAQ_ITEMS.map((item, i) => (
            <FaqItem
              key={item.q}
              q={item.q}
              a={item.a}
              isFirst={i === 0}
            />
          ))}
        </div>
      </section>

      {/* SECTION 8: Final CTA — Ink near-black ground (was Heartwood), matching
          the guarantees and marketplace bands so the page's dark sections read as
          one Ink family. On-dark text (parchment / brass / the single spark CTA)
          all read the same or better on Ink. */}
      <section
        style={{
          padding: '128px 32px',
          background: '#0F1714',
          color: 'var(--parchment)',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          {/* Closing hero lede — the lyrical "an idea, taken seriously..."
              sentence, staged into three registers: a quiet uppercase
              setup, then three parallel serif outcomes with brass bracketing
              the value anchor ("wealth you build on,") and the legacy landing
              ("the world."). Brass-only — spark stays the CTA button below,
              so this closing viewport keeps its single spark. */}
          <h2 className="closelede" ref={closeLedeRef}>
            <span className="closelede__setup">An idea, taken seriously, can become</span>
            <span className="closelede__out closelede__out--1 closelede__out--brass">wealth you build on,</span>
            <span className="closelede__out closelede__out--2">a business you run,</span>
            <span className="closelede__climax">
              or simply a record of the contribution you made to{' '}
              <span className="closelede__out--brass">the world.</span>
              <span className="closelede__caret" aria-hidden />
            </span>
          </h2>
          <p className="closesub">
            You have sparks. We help you find the right one to turn into an
            invention, a product, and a business.
          </p>
          <p
            style={{
              fontSize: 16,
              maxWidth: 600,
              margin: '0 auto 44px',
              lineHeight: 1.6,
              // On-dark brass — matches the heading accents above so the two
              // golds harmonize in the same viewport.
              color: '#c79a4e',
              fontWeight: 600,
              letterSpacing: '0.005em',
            }}
          >
            The downside of trying is that you learn something.
            <br />
            The downside of never trying is that you never know.
          </p>
          {/* Tester-quote rotator — moved here from the hero. Real early-tester
              voices as the final social-proof beat right before the closing CTA
              (proof, then the ask). The section is dark heartwood, matching the
              rotator's existing on-dark styling. */}
          <div className="closing-testimonials">
            <CredibilityRotator />
          </div>
          {/* End-of-page CTA. Routes visitors to the hero textarea
              rather than the sign-up flow — letting them enter their
              idea anonymously first matches the rest of the page's
              "no signup needed to try" framing. The smooth scroll +
              focus brings them right to the input field. */}
          {/* The page's closing conversion CTA — a START action, so spark
              green (momentum), the one green button in this closing viewport. */}
          <Button
            variant="spark"
            // on-dark switches the fill to the brighter spark-light (#4FD992) —
            // the same spark green the "Dash" wordmark uses on heartwood grounds,
            // so the button and the logo green match on this dark section.
            className="btn--on-dark"
            onClick={scrollToHeroIdea}
          >
            Act on my first idea, free →
          </Button>
          {/* "Not ready" escape hatch — for visitors at the end of the
              page who aren't ready to test or commit yet. Surfaces the
              Education Center as the explore-more option, matching the
              two pill CTAs in the hero. */}
          <div style={{ marginTop: 18 }}>
            <Link
              href="/learn"
              className="cta-pill-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 18px',
                borderRadius: 999,
                border: '1.5px solid var(--brass)',
                // Lighter brass for AA text contrast on the heartwood band
                // (bright --brass is only ~3.8:1 here); border stays --brass.
                color: '#C79A4E',
                fontFamily: 'var(--f-mono)',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                textDecoration: 'none',
              }}
            >
              Or browse the Education Center →
            </Link>
          </div>
          {/* Twin Sign up / Sign in row beneath the primary CTA. The
              primary CTA is for anonymous testing; this row is for
              visitors who already know they want to commit (either
              direction).

              The outer container is a flex column at all widths now.
              The "Ready to save your work?" question sits on its own
              line; the two links sit on a separate row below it
              (`landing-final-account-links`). This is far cleaner on
              mobile than the previous all-in-one flex-wrap row, where
              the bullet separator and links would land in awkward
              positions when the row wrapped. */}
          <div
            className="landing-final-account"
            style={{
              marginTop: 28,
              fontSize: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <span style={{ color: 'var(--text-on-dark-muted)' }}>
              Creating an account is free — save your work and pick up anytime.
            </span>
            <div
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                className="final-account-link"
                onClick={() => openAuthModal('signup')}
                style={{
                  color: '#C79A4E', // AA brass on heartwood (hover brightens to --brass-light)
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  fontWeight: 700,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                }}
              >
                Sign up free
              </button>
              <span style={{ opacity: 0.4 }}>·</span>
              <button
                type="button"
                className="final-account-link"
                onClick={() => openAuthModal('signin')}
                style={{
                  color: '#C79A4E', // AA brass on heartwood (hover brightens to --brass-light)
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                }}
              >
                Sign in
              </button>
            </div>
          </div>

          {/* Brand signature line — the permanent positioning anchor.
              Sits as the closing line of the entire page so visitors
              leave with one specific category line in mind. Matches
              the section label at the top of the hero. */}
          <div
            style={{
              marginTop: 72,
              paddingTop: 32,
              borderTop: '1px solid rgba(244, 239, 226, 0.12)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <div style={{ color: 'var(--brass-light)' }}>
              {/* Full lockup (mark + wordmark) on the ink sign-off band. */}
              <BrandLogo size={24} onDark />
            </div>
            <div
              style={{
                fontFamily: 'var(--f-mono)',
                fontSize: 12,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                // "Spark to business" is EntreDash's canonical positioning line
                // (per the identity spec), always set in brass — replacing the
                // pre-rebrand "The inventor's first stop".
                color: 'var(--brass-light)',
              }}
            >
              Spark to business
            </div>
            {/* The full rolling "Featured on" marquee, relocated here from the
                top of the hero — it closes the page under the brand tagline, and
                the hero now leads with the curated two-logo "Listed on" row. */}
            <div className="lclose__badges">
              <ListedOnStrip variant="marquee" label="Featured on" />
            </div>
          </div>
        </div>
      </section>
      </main>

      <style>{`
        /* Compact two-zone commercial panel — the paid-first section lead. A
           full-saturation Heartwood brand surface (like the hero), not a tint.
           LEFT = the pitch, RIGHT = the offer, split by a brass divider.
           On-dark brass is #C79A4E and parchment text carries the payoff. */
        .pvpanel {
          background: var(--heartwood);
          border-radius: 12px;
          /* Tightened vertical padding to keep the panel compact. */
          padding: clamp(22px, 2.8vw, 34px) clamp(28px, 4vw, 52px);
          box-shadow: inset 0 1px 0 rgba(199, 154, 78, 0.30),
            0 1px 2px rgba(15, 23, 20, 0.06),
            0 20px 48px rgba(15, 23, 20, 0.14);
          margin: 0 0 20px;
        }
        .pvpanel__main {
          display: flex;
          align-items: center;
          gap: clamp(24px, 2.5vw, 36px);
        }
        .pvpanel__pitch {
          flex: 1 1 58%;
          display: flex;
          flex-direction: column;
          /* Tighter vertical rhythm between claim, triad, and legacy. */
          gap: clamp(12px, 1.4vw, 16px);
        }
        /* THE CLAIM — one stacked serif statement that fuses the odds-math and
           the lunch anchor into a single argument. All three beats share one
           size so they read co-equal (not a hook + a footnote): capture 36 ->
           you only need one keeper -> it's only a lunch, then the triad + legacy
           below resolve it into the future you're buying. The oversized brass
           "36" is the lone eye-magnet that pulls the reader into the whole
           statement; "one" is the brass keeper. */
        .pvpanel__claim {
          margin: 0;
          font-family: var(--f-serif);
          color: var(--parchment);
          letter-spacing: -0.02em;
          display: flex;
          flex-direction: column;
          gap: 6px;
          /* Wide enough that each beat stays one line — "You only need ONE to
             hold up." sits right at the old 30ch edge, so give it headroom. The
             pitch zone is wider than this, so it never reaches the divider. */
          max-width: 34ch;
        }
        .pvpanel__claim-line {
          font-size: clamp(26px, 3.2vw, 34px);
          font-weight: 400;
          line-height: 1.12;
          /* Each beat holds on its own single line on desktop (so the first two
             beats read as 2 lines, not 3). Mobile re-enables wrapping below. */
          white-space: nowrap;
        }
        /* The lunch beat is the claim's climax and the section's value-spark:
           spark green + uppercase. Prominence comes from case + color (a
           different axis), so the beat stays co-equal in SIZE with the other
           two. On-dark spark only (--spark-light); the light-ground --spark
           goes muddy on heartwood. */
        .pvpanel__claim-line--lunch {
          color: var(--spark-light);
          text-transform: uppercase;
          letter-spacing: 0.01em;
          /* Bolder than the other two beats — the spark climax leans in.
             (700 is a real loaded Sentient weight; 600 would synthesize.) */
          font-weight: 700;
          /* Always one line — the uppercase beat should never break across
             "FOR THE PRICE OF A / LUNCH." The pitch zone is wider than the
             30ch claim box, so a nowrap line sits comfortably without reaching
             the divider. */
          white-space: nowrap;
        }
        /* The unmissable figure. Tight line-height so the big numeral does not
           blow open the gap above the next beat; lining figures read as a stat. */
        .pvpanel__fig {
          font-size: 1.32em;
          color: #c79a4e;
          line-height: 0.9;
          font-variant-numeric: lining-nums;
        }
        /* "ONE" — brass + uppercase + bold: the keeper, emphasized hard to
           reinforce the "you only need one" core message. */
        .pvpanel__em {
          color: #c79a4e;
          text-transform: uppercase;
          font-weight: 700;
        }
        /* Flat triad — one level line of inline phrases connected by straight
           arrows (no ascending stagger). The arrows carry the sequence. */
        .pvpanel__triad {
          display: flex;
          align-items: baseline;
          flex-wrap: wrap;
          gap: 8px 12px;
        }
        .pvpanel__phrase {
          font-family: var(--f-serif);
          font-size: clamp(15px, 1.6vw, 18px);
          font-weight: 400;
          line-height: 1.05;
          letter-spacing: -0.01em;
          color: var(--parchment);
          white-space: nowrap;
        }
        .pvpanel__arrow {
          color: rgba(199, 154, 78, 0.65);
          font-size: 16px;
          line-height: 1;
          align-self: baseline;
          transform: none;
        }
        /* Legacy line — a calm resolving coda to the flat triad (no longer a
           summit). Italic, stepped down from the claim, with a brass "legacy"
           tying it to the value thread. */
        .pvpanel__legacy {
          font-family: var(--f-serif);
          font-style: italic;
          font-size: clamp(20px, 2.2vw, 24px);
          font-weight: 400;
          letter-spacing: -0.01em;
          line-height: 1.15;
          color: var(--parchment);
          margin-top: 14px;
        }
        .pvpanel__legacy-key { color: #c79a4e; }
        .pvpanel__divider {
          width: 1px;
          align-self: stretch;
          background: rgba(199, 154, 78, 0.25);
        }
        /* Fixed width so the long billing subnote wraps INSIDE the offer zone
           instead of stretching it wide and squeezing the pitch (which forced
           the triad to wrap). Mobile overrides this to full width below. */
        .pvpanel__offer {
          flex: 0 0 auto;
          /* Widened 220 → 252px so the longer advisory-access note breathes
             (was tuned for the short "A small bill. A long bet."). */
          width: 252px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .pvpanel__price {
          font-family: var(--f-serif);
          font-size: clamp(34px, 3.6vw, 42px);
          font-weight: 400;
          letter-spacing: -0.02em;
          line-height: 1;
          color: #c79a4e;
        }
        .pvpanel__price-unit {
          font-family: var(--f-sans);
          font-size: 18px;
          font-weight: 500;
          color: rgba(244, 239, 226, 0.8);
          margin-left: 6px;
        }
        /* Advisory-access reframe — two lines. Line 1 (the board) is a muted
           parchment setup; line 2 (the rate contrast) is the spark-green punch,
           echoing the panel's serif voice so it reads as a value note, a step
           above the muted sans billing subnote below it. */
        .pvpanel__note {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .pvpanel__note-board {
          font-family: var(--f-serif);
          font-style: italic;
          font-size: 14px;
          line-height: 1.35;
          color: rgba(244, 239, 226, 0.72);
        }
        .pvpanel__note-rate {
          font-family: var(--f-serif);
          font-style: italic;
          font-size: 15px;
          line-height: 1.35;
          color: var(--spark-light);
        }
        .pvpanel__sub {
          font-family: var(--f-sans);
          font-size: 13.5px;
          line-height: 1.5;
          color: rgba(244, 239, 226, 0.7);
          margin-top: 10px;
        }
        /* Parchment "Subscribe" pill — the panel's one action (paid-first goal).
           Parchment fill, warming to brass on hover (brass = the reward signal).
           Background shift only, no transform — per the brand motion rule. */
        .pvpanel__cta {
          margin-top: 22px;
          display: inline-flex;
          align-items: center;
          background: var(--parchment);
          color: var(--heartwood);
          font-family: var(--f-sans);
          font-size: 15px;
          font-weight: 600;
          padding: 12px 28px;
          border: none;
          border-radius: 999px;
          cursor: pointer;
          text-decoration: none;
          transition: background 120ms ease;
        }
        .pvpanel__cta:hover {
          background: #c79a4e;
        }
        /* Demoted free fallback line, sitting quietly under the panel. */
        .pvfallback {
          font-family: var(--f-sans);
          font-size: 15px;
          color: var(--ink);
          opacity: 0.8;
          margin: 0 0 26px;
        }
        .pvfallback__link {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font: inherit;
          color: var(--brass-deep);
          text-decoration: underline;
          text-underline-offset: 3px;
          font-weight: 600;
        }

        /* Closing-hero lede — the staged "an idea, taken seriously..." heading
           on the dark Heartwood band. On-dark brass #C79A4E only. */
        .closelede {
          margin: 0 0 22px;
        }
        .closelede__setup {
          display: block;
          font-family: var(--f-sans);
          /* Bigger + brighter (founder request) so the setup line reads as a
             confident statement, not a faint kicker — was 13px / 0.55 opacity. */
          font-size: clamp(15px, 1.7vw, 20px);
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(244, 239, 226, 0.85);
          margin-bottom: 22px;
          line-height: 1.4;
        }
        .closelede__out,
        .closelede__climax {
          display: block;
          font-family: var(--f-serif);
          font-size: clamp(28px, 3.6vw, 40px);
          font-weight: 400;
          line-height: 1.14;
          letter-spacing: -0.015em;
          color: var(--parchment);
        }
        .closelede__out--brass {
          color: #c79a4e;
        }
        .closelede__climax {
          text-wrap: balance;
          margin-top: 2px;
        }
        .closesub {
          font-family: var(--f-sans);
          font-size: 17px;
          line-height: 1.6;
          color: rgba(244, 239, 226, 0.82);
          max-width: 600px;
          margin: 0 auto 28px;
        }
        /* Brass block caret — reserved width always (opacity lifecycle only),
           so the centered climax never twitches when it appears/dissolves. */
        .closelede__caret {
          display: inline-block;
          width: 0.5ch;
          height: 0.92em;
          margin-left: 0.12em;
          vertical-align: -0.08em;
          background: #c79a4e;
          opacity: 0;
        }
        /* Line-by-line "ink wipe" reveal. The hidden/armed state is applied by
           JS (.is-armed), so a no-JS render shows the full heading. Each serif
           line wipes left-to-right via a feathered mask sweep; only
           mask-position / opacity / a small transform animate, so there is zero
           layout shift (the lines hold their final box from first paint). */
        .closelede.is-armed .closelede__setup {
          opacity: 0;
          transform: translateY(6px);
        }
        .closelede.is-armed .closelede__out,
        .closelede.is-armed .closelede__climax {
          -webkit-mask-image: linear-gradient(90deg, #000 45%, transparent 55%);
          mask-image: linear-gradient(90deg, #000 45%, transparent 55%);
          -webkit-mask-size: 200% 100%;
          mask-size: 200% 100%;
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
          -webkit-mask-position: 100% 0;
          mask-position: 100% 0;
          opacity: 0;
        }
        /* Slower, deliberate type-out. Delays/durations are timed against the
           JS loop's TYPE_OUT_MS (~4600ms): setup rises, then three lines wipe
           in sequence, then the caret settles. */
        .closelede.is-revealing .closelede__setup {
          animation: closeRise 600ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .closelede.is-revealing .closelede__out--1 {
          animation: closeWipe 900ms cubic-bezier(0.4, 0, 0.2, 1) 500ms forwards;
        }
        .closelede.is-revealing .closelede__out--2 {
          animation: closeWipe 900ms cubic-bezier(0.4, 0, 0.2, 1) 1500ms forwards;
        }
        .closelede.is-revealing .closelede__climax {
          animation: closeWipe 1000ms cubic-bezier(0.4, 0, 0.2, 1) 2600ms forwards;
        }
        .closelede.is-revealing .closelede__caret {
          animation: closeCaret 1800ms ease-in-out 3700ms forwards;
        }
        /* Loop fade-out — the whole heading dissolves before it re-types. */
        .closelede.is-clearing {
          opacity: 0;
          transition: opacity 500ms ease;
        }
        @keyframes closeRise {
          to { opacity: 1; transform: none; }
        }
        @keyframes closeWipe {
          0% { -webkit-mask-position: 100% 0; mask-position: 100% 0; opacity: 0; }
          15% { opacity: 1; }
          100% { -webkit-mask-position: 0 0; mask-position: 0 0; opacity: 1; }
        }
        @keyframes closeCaret {
          0% { opacity: 0; }
          12% { opacity: 1; }
          34% { opacity: 0.15; }
          50% { opacity: 1; }
          72% { opacity: 0.15; }
          84% { opacity: 1; }
          100% { opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .closelede.is-armed .closelede__setup,
          .closelede.is-armed .closelede__out,
          .closelede.is-armed .closelede__climax {
            opacity: 1;
            transform: none;
            -webkit-mask-image: none;
            mask-image: none;
          }
          .closelede.is-armed .closelede__caret { opacity: 0; }
        }

        /* Annual / Monthly billing toggle — a segmented pill on the parchment
           ground (light-ground brass tokens are correct here). */
        .billtoggle {
          display: flex;
          align-items: center;
          gap: 14px;
          margin: 0 0 28px;
        }
        .billtoggle__track {
          position: relative;
          display: inline-flex;
          padding: 4px;
          background: rgba(27, 58, 46, 0.06);
          border: 1px solid var(--border-hairline);
          border-radius: 999px;
        }
        .billtoggle__seg {
          position: relative;
          z-index: 1;
          border: none;
          background: none;
          cursor: pointer;
          font-family: var(--f-sans);
          font-size: 14px;
          font-weight: 600;
          padding: 8px 0;
          width: 96px;
          text-align: center;
          border-radius: 999px;
          color: var(--ink);
          transition: color 200ms ease;
        }
        .billtoggle__seg[aria-selected='true'] {
          color: var(--parchment);
        }
        /* Sliding thumb sits under the active segment. Equal-width segments
           (96px) make the translate exact. */
        .billtoggle__thumb {
          position: absolute;
          top: 4px;
          left: 4px;
          bottom: 4px;
          width: 96px;
          background: var(--heartwood);
          border-radius: 999px;
          transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .billtoggle__thumb[data-billing='monthly'] {
          transform: translateX(96px);
        }
        .billtoggle__save {
          font-family: var(--f-mono);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--brass-deep);
          background: rgba(181, 133, 60, 0.12);
          padding: 4px 10px;
          border-radius: 999px;
          transition: opacity 200ms ease;
        }
        /* Fades out on Monthly but stays in flow so nothing shifts. */
        .billtoggle__save[data-on='false'] {
          opacity: 0;
        }
        /* Top-up disclosure — collapses the skippable top-up mechanics into a
           tap target so it isn't a prose wall above the plan grid (worst on
           mobile). Native <details>; styled to the section's mono/brass voice. */
        .pricing-overage__more {
          margin-top: 8px;
        }
        .pricing-overage__more > summary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          list-style: none;
          font-family: var(--f-mono);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--brass-deep);
        }
        .pricing-overage__more > summary::-webkit-details-marker { display: none; }
        .pricing-overage__chev {
          width: 7px;
          height: 7px;
          border-right: 1.5px solid currentColor;
          border-bottom: 1.5px solid currentColor;
          transform: rotate(45deg);
          transition: transform 160ms ease;
          margin-top: -3px;
        }
        .pricing-overage__more[open] .pricing-overage__chev {
          transform: rotate(-135deg);
          margin-top: 2px;
        }
        .pricing-overage__body {
          margin: 10px auto 0;
          max-width: 620px;
          font-size: 14px;
          line-height: 1.6;
          color: var(--text-decorative);
          font-style: italic;
        }
        @media (prefers-reduced-motion: reduce) {
          .pricing-overage__chev { transition: none; }
        }
        /* Price numeral re-mounts on toggle (keyed) and replays this fade. */
        @keyframes pvprice-in {
          from { opacity: 0; transform: translateY(3px); }
          to { opacity: 1; transform: none; }
        }
        .price-amt {
          display: inline-block;
          min-width: 3.4ch;
          animation: pvprice-in 180ms ease both;
        }
        @media (prefers-reduced-motion: reduce) {
          .billtoggle__thumb { transition: none; }
          .price-amt { animation: none; }
        }

        /* Plan comparison matrix (collapsible). Heartwood checks, a calm mist
           rule for not-included (never an X), Standard column tinted. */
        /* Prominent, obviously-clickable full-width bar that gateways the
           comparison table. Outline + faint tint (never filled) so it stays
           quieter than Standard's spark button in the same viewport. */
        .cmp-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 16px 24px;
          background: rgba(181, 133, 60, 0.05);
          border: 1px solid var(--brass);
          border-radius: 12px;
          cursor: pointer;
          font-family: var(--f-mono);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--brass-deep);
          transition: background 140ms ease, border-color 140ms ease;
        }
        .cmp-toggle:hover {
          background: rgba(181, 133, 60, 0.10);
          border-color: var(--brass-deep);
        }
        .cmp-toggle__chev {
          font-size: 14px;
          color: var(--brass-deep);
          transition: transform 220ms ease;
        }
        .cmp-toggle__chev.is-open {
          transform: rotate(180deg);
        }
        /* "Everything in X, plus" inheritance line above each paid tier's
           net-new bullets. */
        .tier-inherit {
          font-family: var(--f-mono);
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--brass-deep);
          /* Keep the "Everything in X, plus" line on one row so Standard and
             Pro's inheritance lines sit at the same height. */
          white-space: nowrap;
          padding-bottom: 14px;
          margin-bottom: 14px;
          border-bottom: 1px solid var(--border-hairline);
        }
        .cmp-wrap {
          margin-top: 28px;
          animation: cmp-in 280ms ease both;
        }
        @keyframes cmp-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: none; }
        }
        .cmp-scrollhint {
          display: none;
          font-family: var(--f-mono);
          font-size: 11px;
          letter-spacing: 0.06em;
          color: var(--text-decorative);
          text-align: right;
          margin-bottom: 8px;
        }
        .cmp {
          width: 100%;
          max-width: 980px;
          margin: 0 auto;
          border-collapse: collapse;
        }
        .cmp th,
        .cmp td {
          padding: 14px 18px;
          text-align: center;
          border-bottom: 1px solid var(--border-hairline);
        }
        .cmp th {
          vertical-align: bottom;
        }
        .cmp__lbl {
          text-align: left;
          width: 34%;
          font-size: 15px;
          color: var(--ink);
        }
        .cmp__plan {
          font-family: var(--f-serif);
          font-size: 18px;
          font-weight: 500;
        }
        .cmp__price {
          font-family: var(--f-mono);
          font-size: 11px;
          font-weight: 600;
          color: var(--text-decorative);
          margin-top: 4px;
        }
        .cmp__badge {
          display: inline-block;
          background: var(--brass);
          color: var(--parchment);
          font-family: var(--f-mono);
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 3px 8px;
          border-radius: 999px;
          margin-bottom: 6px;
        }
        /* Starter's "Start free" CTA. It sits beside three saturated fills
           (heartwood / spark / brass) so it must stay visually lighter — but
           the default .btn--secondary hairline on transparent is almost
           invisible against the cream ground, which made the free path look
           disabled. A crisp 2px ink outline over the card surface reads as a
           real, tappable button while still deferring to the paid CTAs. */
        .plan-cta--free.btn--secondary {
          background: var(--surface-card);
          border-width: 2px;
          border-color: var(--ink);
          color: var(--ink);
          font-weight: 700;
        }
        .plan-cta--free.btn--secondary:hover:not(:disabled) {
          background: var(--ink);
          border-color: var(--ink);
          color: var(--parchment);
        }
        .cmp__std {
          background: var(--surface-card);
        }
        .cmp thead .cmp__std {
          border-top: 2px solid var(--brass);
          border-radius: 10px 10px 0 0;
        }
        /* Plus column tint mirrors the featured column in the ladder above, so
           the capability boundary shows up in the same place in both views. */
        .cmp__plus {
          background: rgba(31, 157, 99, 0.05);
        }
        .cmp thead .cmp__plus {
          border-top: 2px solid var(--spark);
          border-radius: 10px 10px 0 0;
        }
        /* With ~17 rows and horizontal scroll, losing the row labels once you
           scroll right makes the matrix unreadable. Pin the label column. */
        .cmp__lbl {
          position: sticky;
          left: 0;
          z-index: 2;
          background: var(--surface-page);
        }
        .cmp__grp td {
          font-family: var(--f-mono);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--brass-deep);
          text-align: left;
          padding-top: 28px;
          border-bottom: none;
        }
        .cmp__cap td {
          text-align: left;
          font-size: 12.5px;
          font-style: italic;
          color: var(--text-decorative);
          padding-top: 2px;
          padding-bottom: 12px;
          border-bottom: none;
        }
        /* Spark overflow-pack add-on line. Sits between the pay-per-invention
           band and the comparison toggle as a calm full-width strip — a brass
           glyph, the pitch, and the price pinned right. Reads as a companion to
           the ppi band (both are no-subscription extras), not a fifth plan. */
        .pricing-stripe-trust {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 24px;
          font-size: 13px;
          /* Muted ink — the whole line recedes; the wordmark is the only
             element carrying a hair of extra weight (see .stripe-mark). */
          color: rgba(15, 23, 20, 0.55);
        }
        /* Lock sits at text tone so it disappears into the line — a scannable
           anchor, not a competing element. */
        .pricing-stripe-trust .pst-lock { color: rgba(15, 23, 20, 0.55); flex: none; }
        /* Stripe wordmark: optical cap-match to the 13px text, one notch darker
           than the surrounding ink so it stays legible without becoming the
           focal point. Baseline nudge — the logotype metrics sit high. */
        .pricing-stripe-trust .stripe-mark {
          height: 14px;
          width: auto;
          flex: none;
          color: rgba(15, 23, 20, 0.72);
          transform: translateY(1px);
        }
        .spark-addon {
          display: flex;
          align-items: center;
          gap: 18px;
          margin-top: 28px;
          padding: 18px 24px;
          background: rgba(181, 133, 60, 0.06);
          border: 1px solid rgba(181, 133, 60, 0.24);
          border-radius: var(--r-md);
        }
        .spark-addon__glyph {
          flex: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(181, 133, 60, 0.14);
          color: var(--brass);
        }
        .spark-addon__body { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1; }
        .spark-addon__title { font-family: var(--f-serif); font-size: 16px; font-weight: 500; color: var(--ink); }
        .spark-addon__desc { font-size: 13.5px; line-height: 1.5; color: color-mix(in srgb, var(--ink) 70%, transparent); }
        .spark-addon__price {
          flex: none;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 1px;
          padding-left: 12px;
        }
        .spark-addon__amt { font-family: var(--f-serif); font-size: 20px; color: var(--brass-deep); line-height: 1; }
        .spark-addon__unit { font-family: var(--f-mono); font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-decorative); }
        @media (max-width: 640px) {
          .spark-addon { flex-wrap: wrap; gap: 14px; padding: 18px; }
          .spark-addon__glyph { width: 34px; height: 34px; }
          .spark-addon__price { flex-direction: row; align-items: baseline; gap: 6px; padding-left: 0; width: 100%; }
        }
        .cmp__yes {
          color: var(--heartwood);
          font-size: 17px;
          font-weight: 700;
        }
        .cmp__val {
          font-size: 15px;
          color: var(--ink);
        }
        /* Not-included = a calm mist rule, never a punitive X. */
        .cmp__no {
          display: inline-block;
          width: 12px;
          height: 2px;
          border-radius: 1px;
          background: var(--mist);
          opacity: 0.7;
        }
        .cmp tbody tr:hover td {
          background: rgba(27, 58, 46, 0.03);
        }
        .cmp tbody tr:hover td.cmp__std {
          background: var(--surface-card);
        }
        /* Mobile: horizontal scroll with a frozen feature-label column. */
        @media (max-width: 680px) {
          /* The table scrolls WITHIN this wrap (page never pans), with the
             feature-label column frozen and a "scroll to compare" hint. Cell
             density is tightened so 2-3 plan columns are visible per scroll
             instead of one. */
          .cmp-wrap {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .cmp {
            min-width: 500px;
          }
          .cmp th,
          .cmp td {
            padding: 9px 10px;
          }
          .cmp__lbl {
            position: sticky;
            left: 0;
            background: var(--surface-page);
            width: 42%;
            font-size: 13px;
            box-shadow: 1px 0 0 var(--border-hairline);
          }
          .cmp__plan {
            font-size: 15px;
          }
          .cmp__price {
            font-size: 10px;
          }
          .cmp-scrollhint {
            display: block;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .cmp-wrap { animation: none; }
          .cmp-toggle__chev { transition: none; }
        }

        /* Stack the two zones on mobile; flatten the triad climb. */
        @media (max-width: 820px) {
          .pvpanel__main {
            flex-direction: column;
            align-items: flex-start;
            gap: 24px;
          }
          /* Allow the claim beats to wrap on narrow screens. */
          .pvpanel__claim-line,
          .pvpanel__claim-line--lunch {
            white-space: normal;
          }
          .pvpanel__divider {
            width: 100%;
            height: 1px;
            align-self: auto;
          }
          .pvpanel__offer { width: 100%; }
          .pvpanel__cta { width: 100%; justify-content: center; }
        }

        /* Tablet: pricing grid is too tight at 4 columns; fall back to
           2x2 and zero out padding-edge tweaks since every cell now has a
           neighbor on its right OR an adjacent row, not the full row. */
        @media (max-width: 1100px) and (min-width: 801px) {
          .price-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 48px 0 !important;
          }
          .price-grid > div {
            border-right: none !important;
            padding: 0 24px !important;
          }
          .price-grid > div:nth-child(odd) {
            padding-left: 0 !important;
            border-right: 1px solid var(--border-hairline) !important;
          }
          .price-grid > div:nth-child(even) {
            padding-right: 0 !important;
          }
        }
        @media (max-width: 800px) {
          .how-grid, .learn-grid, .journey-grid, .price-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .journey-cell {
            border-right: none !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            border-top: 1px solid var(--border-hairline) !important;
          }
          .price-grid > div {
            border-right: none !important;
            border-bottom: 1px solid var(--border-hairline);
            padding: 24px 0 !important;
          }
        }
      `}</style>

      {/* Global mobile cleanup pass.

          The landing page was designed at desktop widths; at narrow
          viewports several sections overflow because:
          - section padding is fixed at 32px horizontal (eats 64px of
            a 375px screen, leaving 311px for content)
          - many sections set `padding: '128px 32px'` which is too
            much vertical breathing room on a phone
          - hero headline starts at 44px on mobile which is large
            for a 311px content column
          - typography clamps that look generous on desktop become
            cramped on mobile

          One consolidated breakpoint at 640px handles all of it.
          Targeting `.landing-section` and the hero specifically
          keeps the rules narrow — no risk of bleeding into other
          pages.

          The 480px breakpoint covers true phone widths with even
          tighter padding and smaller headlines. */}
      <style>{`
        /* Hero base styles — viewport-height-aware so it fits Safari
           laptop viewports (~700-720px tall) without the bottom CTA
           row falling below the fold. The clamp() padding scales with
           the actual viewport height: tight on short screens (where
           the hero is barely tall enough for its content), generous
           on tall monitors.

           min-height has two declarations on purpose:
             1. 100vh — fallback for any browser that doesn't support
                dvh, plus a guaranteed-correct floor for the modern
                ones.
             2. 100dvh — modern dynamic viewport (Safari 15.4+,
                Chrome 108+, Firefox 101+). Accounts for the mobile
                URL bar collapsing on scroll. Browsers that
                understand this declaration use it; older browsers
                ignore it and stick with 100vh from line 1.
           Order matters: declarations are read top-to-bottom and the
           last valid one wins, so 100dvh overrides 100vh when
           supported. */
        .landing-hero {
          min-height: 100vh;
          min-height: 100dvh;
          padding: clamp(88px, 11vh, 120px) 32px clamp(56px, 7vh, 96px);
        }
        @media (max-width: 640px) {
          /* Every body section uses padding: '128px 32px'. Shrink
             both axes — tighter horizontal padding gives the content
             more room; tighter vertical padding makes the page less
             interminable on a phone. The hero is excluded because it
             has its own rule below (different background, different
             min-height behaviour). */
          /* Exclude the ACTUAL hero. The hero section's class is .lhero, not
             .landing-hero (that class is never applied anywhere — the whole
             .landing-hero block in this file is stale). With the old
             :not(.landing-hero) the hero was NOT excluded, so it wrongly inherited
             this content-section top/bottom padding (72px here, 56px at 480px) —
             the big dead gap between the nav and the eyebrow. :not(.lhero) excludes
             it correctly so the hero keeps its own tight top padding. */
          .landing-page-body > main > section:not(.lhero) {
            padding-left: 20px !important;
            padding-right: 20px !important;
            padding-top: 72px !important;
            padding-bottom: 72px !important;
          }
          /* Hero: smaller padding so the headline has room to wrap
             without being clipped by overflowing words. */
          .landing-hero {
            padding: 96px 20px 64px !important;
            min-height: auto !important;
          }
          /* Section headlines (clamp 36/48/56) shrink further on
             phone so long titles like "From your first random idea
             to a granted patent" don't overflow the 335px content
             column. */
          .landing-page-body > main > section:not(.landing-hero) h2.f-display,
          .landing-page-body > main > section:not(.landing-hero) h1.f-display {
            font-size: clamp(28px, 8vw, 36px) !important;
            line-height: 1.15 !important;
          }
          /* Hero headline is bigger by design; cap at 44px on phone
             so "Every great invention started as someone's random
             idea." wraps cleanly within the content column. */
          .landing-hero h1.f-display {
            font-size: clamp(32px, 9vw, 44px) !important;
            line-height: 1.1 !important;
            margin-bottom: 18px !important;
          }
          /* Body paragraphs shrink slightly so a 16px line at 6 words
             per line on mobile drops back to readable density. */
          .landing-page-body > main > section:not(.landing-hero) p {
            font-size: 15px;
            line-height: 1.6;
          }
          /* Mobile hero hierarchy: the HEADLINE is the star. The eyebrow is a
             crisp brass kicker sized to read as one tight line above it (not
             the desktop 17-21px, which wrapped the long hook to 3 lines and
             competed with the headline). The subheading is CUT on mobile so the
             kicker + big two-tone headline + CTA own the first screen. */
          .landing-hero .lhero__eyebrow {
            font-size: 14px !important;
            letter-spacing: 0.1em !important;
            margin-bottom: 14px !important;
          }
          .landing-hero .lhero__eyebrow::before {
            width: 40px !important;
            margin-bottom: 10px !important;
          }
          .landing-hero .lhero__headline {
            font-size: clamp(40px, 11.5vw, 54px) !important;
            line-height: 1.0 !important;
            letter-spacing: -0.035em !important;
            max-width: none !important;
            margin-bottom: 16px !important;
          }
          /* Subheading hidden on mobile — the headline + CTA lead. */
          .landing-hero .lhero__sub {
            display: none !important;
          }
          /* Panel header: keep the arc headline, drop the expendable framing
             chip so three stacked cards don't push the panel too tall. */
          .landing-hero .hero-showcase__growth {
            font-size: 22px !important;
            line-height: 1.15 !important;
          }
          .landing-hero .hero-showcase__framing {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          /* Same fix as above at the 480px breakpoint: exclude the real hero
             (.lhero) so it does not inherit this 56px content padding. */
          .landing-page-body > main > section:not(.lhero) {
            padding-left: 16px !important;
            padding-right: 16px !important;
            padding-top: 56px !important;
            padding-bottom: 56px !important;
          }
          .landing-hero {
            padding: 88px 16px 56px !important;
          }
        }

        /* Educator + Enterprise organization band CSS removed.
           See the comment block in the pricing section above for
           the rationale; the bands themselves no longer render so
           there's nothing to style. */
      `}</style>

      {/* Light A/B variant skin — rendered LAST and scoped under
          `.landing--light`, so every rule outranks the base (higher specificity
          + later source order) without needing !important. Same content and
          funnel; only the visual system changes. See LANDING_LIGHT_CSS. */}
      {light && <style>{LANDING_LIGHT_CSS}</style>}

      {/* Newsletter signup band + standard link rows. The newsletter
          band is inside SiteFooter so the same component covers
          footer-of-Landing, footer-of-/try, and the public legal pages. */}
      <SiteFooter />

      {/* Confidentiality gate for anonymous "Test my idea" submissions.
          Lazy-loaded and mounted only while open, so its chunk isn't fetched
          until a visitor first triggers the gate. On accept it fires
          `commitStart` with the current text, which writes anon
          state and navigates to /try. */}
      {showConfidentiality && (
        <Suspense fallback={null}>
          <ConfidentialityModal
            anonymous
            open
            onAccept={() => {
              setShowConfidentiality(false);
              commitStart(ideaText.trim());
            }}
            onClose={() => setShowConfidentiality(false)}
          />
        </Suspense>
      )}

      {/* The Spark tool in a phone-frame modal, launched from the hero's
          "No formed idea yet?" entry. Mounted only while open, so each open
          starts the funnel fresh. */}
      {sparkOpen && (
        <Suspense fallback={null}>
          <SparkModal open onClose={() => setSparkOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}

